import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Select, Input, Button, Checkbox, Spin, Empty, ConfigProvider, Modal, Radio } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, StopOutlined, DownloadOutlined } from '@ant-design/icons';
import PageHeader from 'components/pageHeader';
import PageDataLoader from 'components/pageDataLoader';
import Notification from 'services/Notification';
import { startAiTestHandoff } from 'actions/testAdmin';
import dropdownArrow from '../../../Assets/schedule/dropdownArrow.svg';
import MathText from './MathText';
import { saveGenerationSnapshot, getGenerationSnapshot } from './generationCache';
import { downloadPaperPdf } from './paperPdf';

const API_BASE = 'https://aitestnew.aulas.in';
// Generation is parallelized per subject on the backend, taking ~30-60s.
// Keep client timeout at 300s (5 minutes) as specified in Push 5.
const GENERATE_TIMEOUT_MS = 300000;
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const SECTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

const diagramUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${path.startsWith('/api/') ? path : `/api${path}`}`;
};

const formatDetail = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const loc = Array.isArray(item.loc)
          ? item.loc.filter((part) => part !== 'body').join('.')
          : '';
        return loc ? `${loc}: ${item.msg}` : item.msg || JSON.stringify(item);
      })
      .join('; ');
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return '';
};

const shortenError = (raw) => {
  if (!raw) return '';
  let msg = String(raw).trim();
  msg = msg.replace(/^The server rejected these inputs:\s*/i, '');
  if (/Cannot allocate questions/i.test(msg) || /across \d+ chapters/i.test(msg)) {
    const minMatch = msg.match(/at least\s*(\d+)/i);
    const acrossMatch = msg.match(/across\s*(\d+)\s*chapters/i);
    const minCount = minMatch ? minMatch[1] : (acrossMatch ? acrossMatch[1] : null);
    if (minCount) {
      return `Increase questions to at least ${minCount} or select fewer chapters.`;
    }
    return 'Increase number of questions or select fewer chapters.';
  }
  msg = msg.replace(/^(?:Error:\s*|Request failed \(\d+\):\s*)/i, '');
  if (msg && !/[.!?]$/.test(msg)) {
    msg += '.';
  }
  return msg;
};

const QUESTION_TYPES = [
  { value: 'single_mcq', label: 'Single Correct MCQ' },
  { value: 'multi_correct_mcq', label: 'Multi Correct MCQ' },
  { value: 'integer_type', label: 'Numeric Type' },
  { value: 'matrix_match', label: 'Matrix Match' }
];

// Push 8: the Board (CBSE) pattern accepts only these six types. There is no
// dropdown endpoint for them, so the list lives here.
const BOARD_QUESTION_TYPES = [
  { value: 'single_mcq', label: 'Multiple choice' },
  { value: 'assertion_reason', label: 'Assertion-reason' },
  { value: 'short_answer_2', label: 'Short answer (2 marks)' },
  { value: 'short_answer_3', label: 'Short answer (3 marks)' },
  { value: 'case_based', label: 'Case-based (4 marks)' },
  { value: 'long_answer', label: 'Long answer (5 marks)' }
];
// Banking Mains adds 'descriptive' (essay / letter, no options): shown like the Board
// written answers, with a model answer and marking points.
const WRITTEN_ANSWER_TYPES = ['short_answer_2', 'short_answer_3', 'long_answer', 'descriptive'];

// Marking points as { point, marks }, whether they come as objects or plain strings.
const normalizeMarkingPoints = (list) =>
  (Array.isArray(list) ? list : []).map((mp) =>
    (mp && typeof mp === 'object' ? { point: mp.point || mp.text || '', marks: mp.marks } : { point: String(mp ?? ''), marks: undefined }));

// Questions answered in writing and graded by the teacher. They are saved as
// Subjective questions; everything else is objective (auto-scored).
const SUBJECTIVE_QUESTION_TYPES = [...WRITTEN_ANSWER_TYPES, 'case_based', 'subjective'];
// The Board types carry their marks in the name, used when a question arrives without marks.
const BOARD_TYPE_MARKS = { short_answer_2: 2, short_answer_3: 3, case_based: 4, long_answer: 5 };

const questionTypeOf = (question) =>
  String(question?.question_type ?? question?.type ?? '').toLowerCase().trim();

const isSubjectiveQuestion = (question) => SUBJECTIVE_QUESTION_TYPES.includes(questionTypeOf(question));

// Marks the generator sent for a written question, else what its Board type implies.
// Deliberately skips marking_scheme_by_type: those schemes are for the objective types.
const getSubjectiveMarks = (question) => {
  const direct = Number(question?.marks ?? question?.marking_scheme?.correct);
  if (Number.isFinite(direct) && direct > 0) return direct;
  return BOARD_TYPE_MARKS[questionTypeOf(question)] ?? null;
};

const TEST_KIND_OPTIONS = [
  { value: 'Mixed', label: 'Mixed test' },
  { value: 'Objective', label: 'Objective test' },
  { value: 'Subjective', label: 'Subjective test' }
];

const isBoardPatternValue = (patternValue) =>
  String(patternValue || '').toLowerCase().includes('board');

// A full CBSE paper follows the fixed blueprint, so its size is known up front.
const getFullBoardBlueprint = (subject) =>
  String(subject || '').toLowerCase().startsWith('math')
    ? { questions: 38, marks: 80 }
    : { questions: 33, marks: 70 };

// Banking (SBI PO, push B1): the "class" is an exam stage (Prelims), subjects are
// sections and chapters are topics. Every question is a five-option MCQ, and some
// topics come as a set of questions on one passage, table or puzzle.
const isBankingPatternValue = (patternValue) =>
  String(patternValue || '').toLowerCase().includes('bank');
// The backend refuses a set topic given fewer questions than this.
const MIN_SET_QUESTIONS = 3;
const MAX_QUESTIONS_BANKING = 100;
const BANKING_PAPER_MODES = [
  { value: 'full', label: 'Full mock' },
  { value: 'sectional', label: 'Full section' },
  { value: 'custom', label: 'Custom' }
];

const BANKING_TERMS = { class: 'Stage', subject: 'Section', chapter: 'topic', chapters: 'Topics' };
const DEFAULT_TERMS = { class: 'Class', subject: 'Subject', chapter: 'chapter', chapters: 'Chapters' };

// What the form is set to, worked out here rather than in the component: its body is
// already so branchy that the hooks lint rule miscounts the paths and flags later hooks.
const bankingFormState = (selection, isMultiSubject) => {
  const isBanking = isBankingPatternValue(selection.pattern);
  const isBankingFull = isBanking && selection.paper_mode === 'full';
  const isBankingSectional = isBanking && selection.paper_mode === 'sectional';
  return {
    isBanking,
    isBankingFull,
    isBankingSectional,
    // A full mock or a full section follows the blueprint: no topics, weightage or count to choose.
    isBankingBlueprintPaper: isBankingFull || isBankingSectional,
    terms: isBanking ? BANKING_TERMS : DEFAULT_TERMS,
    maxQuestions: isBanking ? MAX_QUESTIONS_BANKING : (isMultiSubject ? MAX_QUESTIONS_MULTI : MAX_QUESTIONS_SINGLE)
  };
};

// "+1.5 / -0.375" for a section's marking scheme.
const markingSchemeText = (scheme) => {
  if (!scheme || scheme.correct === undefined || scheme.correct === null) return '';
  const wrong = Number(scheme.incorrect) || 0;
  return wrong ? `+${formatMarks(scheme.correct)} / ${formatMarks(wrong)}` : `+${formatMarks(scheme.correct)}, no negative marking`;
};

// Mains lists sections the backend cannot generate yet (General Awareness needs its
// current-affairs digest) as pending_sections: shown as coming soon, never selectable.
const pendingSectionNames = (stageBlueprint) =>
  (Array.isArray(stageBlueprint?.pending_sections) ? stageBlueprint.pending_sections : [])
    .map((p) => (p && typeof p === 'object' ? p.subject || p.name || p.id : p))
    .filter(Boolean)
    .map(String);

const withPendingSections = (subjectOptions, stageBlueprint) => {
  const have = new Set(subjectOptions.map((o) => o.value));
  return [
    ...subjectOptions,
    ...pendingSectionNames(stageBlueprint)
      .filter((name) => !have.has(name))
      .map((name) => ({ value: name, label: `${name} (coming soon)`, disabled: true }))
  ];
};

const isBankingPaperResult = (generatedTest) => {
  if (!generatedTest) return false;
  return isBankingPatternValue((generatedTest._metadata && generatedTest._metadata.pattern) || generatedTest.exam);
};

const escapeHtml = (text) =>
  String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const proseToHtml = (text) =>
  escapeHtml(text).replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>');

// A set's data table as plain lines, for the PDF and the plain-text passage.
const setTableText = (table) => {
  if (!table || !Array.isArray(table.columns)) return '';
  const lines = [table.columns.join(' | ')];
  (table.rows || []).forEach((row) => lines.push((Array.isArray(row) ? row : [row]).join(' | ')));
  return [table.title, ...lines].filter(Boolean).join('\n');
};

// Directions, passage and table of a set: what every question of the set is read against.
const setPassageText = (set) =>
  [set?.directions, set?.stimulus, setTableText(set?.table)].filter(Boolean).join('\n\n');

const setPassageHtml = (set) => {
  const parts = [];
  if (set?.directions) parts.push(`<p><em>${proseToHtml(set.directions)}</em></p>`);
  if (set?.stimulus) parts.push(`<p>${proseToHtml(set.stimulus)}</p>`);
  const table = set?.table;
  if (table && Array.isArray(table.columns)) {
    const cell = (tag, value) => `<${tag} style="border:1px solid #ccc;padding:4px 8px;">${proseToHtml(value)}</${tag}>`;
    parts.push(
      (table.title ? `<p><strong>${proseToHtml(table.title)}</strong></p>` : '') +
      '<table style="border-collapse:collapse;">' +
      `<tr>${table.columns.map((c) => cell('th', c)).join('')}</tr>` +
      (table.rows || []).map((row) => `<tr>${(Array.isArray(row) ? row : [row]).map((v) => cell('td', v)).join('')}</tr>`).join('') +
      '</table>'
    );
  }
  return parts.join('');
};

// A question's own instruction line goes above it ("The following sentence has been divided into parts..."),
// and a descriptive task's word limit below it.
const withDirections = (question) =>
  [
    question?.directions,
    question?.question || question?.text || '',
    question?.word_limit ? `(Word limit: ${question.word_limit} words)` : ''
  ].filter(Boolean).join('\n\n');

// Push B2.1: a descriptive task offers two prompts (A, B) and the student answers one. Each
// choice carries its own prompt, model answer and marking points. The first B2 build put one
// prompt's model answer and points on the question itself; both shapes are read here.
const descriptiveChoices = (question) =>
  (Array.isArray(question?.choices) ? question.choices : []).filter(Boolean).map((choice, index) => ({
    label: choice.label || OPTION_LETTERS[index] || String(index + 1),
    prompt: choice.prompt || choice.question || '',
    modelAnswer: choice.model_answer || '',
    markingPoints: normalizeMarkingPoints(choice.marking_points)
  }));

// The task as the student reads it: the shared instruction, then each prompt to choose from.
const descriptivePromptText = (question) => {
  const choices = descriptiveChoices(question);
  if (choices.length === 0) return withDirections(question);
  return [
    withDirections(question),
    'Answer any one of the following.',
    ...choices.map((c) => `(${c.label}) ${c.prompt}`)
  ].filter(Boolean).join('\n\n');
};

const markingPointLines = (points) =>
  points.map((mp) => `- ${mp.point}${mp.marks !== undefined && mp.marks !== null ? ` (${formatMarks(mp.marks)})` : ''}`);

// A descriptive task's answer key as one solution text: the model answer, then the marking
// points, for each choice when there are two.
const descriptiveAnswerKey = (question) => {
  const keyFor = (modelAnswer, points) => [
    modelAnswer ? `Model answer:\n${modelAnswer}` : '',
    points.length ? `Marking points:\n${markingPointLines(points).join('\n')}` : ''
  ].filter(Boolean).join('\n\n');
  const choices = descriptiveChoices(question);
  if (choices.length > 0) {
    return choices.map((c) => `Choice ${c.label}\n\n${keyFor(c.modelAnswer, c.markingPoints)}`).join('\n\n');
  }
  return keyFor(question?.model_answer, normalizeMarkingPoints(question?.marking_points));
};

// The question bank keeps a passage and its questions together as one comprehension
// question, so each Banking set goes to Create Test as one, in the place of its first
// question. Sections are renumbered to match.
const collapseBankingSets = (questions, questionSets) => {
  const setById = new Map((questionSets || []).map((set) => [set.set_id, set]));
  const entries = [];
  const groups = new Map();
  questions.forEach((question) => {
    const set = question.set_id ? setById.get(question.set_id) : null;
    if (!set) {
      entries.push({ question });
      return;
    }
    if (!groups.has(set.set_id)) {
      const group = { set, members: [] };
      groups.set(set.set_id, group);
      entries.push({ group });
    }
    groups.get(set.set_id).members.push(question);
  });

  const bySubject = new Map();
  const collapsed = entries.map((entry, index) => {
    const id = index + 1;
    let out;
    if (entry.group) {
      const { set, members } = entry.group;
      const lead = members[0];
      out = {
        id,
        type: 'comprehensive',
        question_type: 'comprehensive',
        question: '',
        paragraph: setPassageText(set),
        paragraph_html: setPassageHtml(set),
        sub_questions: members.map((member) => ({
          question: withDirections(member),
          type: 'single_mcq',
          question_type: 'single_mcq',
          options: member.options || [],
          correct_answer: member.correct_answer,
          explanation: member.explanation || '',
          marks: member.marks,
          negative_marks: member.negative_marks
        })),
        options: [],
        correct_answer: '',
        explanation: set.solution || '',
        chapter: set.topic || lead.chapter,
        topic: set.topic || lead.topic,
        subject: lead.subject,
        difficulty: lead.difficulty
      };
    } else if (String(entry.question.question_type || '').toLowerCase() === 'descriptive') {
      // A Mains essay or letter goes over as a subjective question; its answer key becomes the solution.
      out = {
        ...entry.question,
        id,
        type: 'descriptive',
        question_type: 'descriptive',
        question: descriptivePromptText(entry.question),
        options: [],
        explanation: descriptiveAnswerKey(entry.question) || entry.question.explanation || ''
      };
    } else {
      out = { ...entry.question, id, question: withDirections(entry.question) };
    }
    if (out.subject) {
      if (!bySubject.has(out.subject)) bySubject.set(out.subject, []);
      bySubject.get(out.subject).push(id);
    }
    return out;
  });
  const sections = Array.from(bySubject.entries()).map(([subject, ids]) => ({ subject, question_ids: ids }));
  return { questions: collapsed, sections };
};

const DIFFICULTY_MIX_LEVELS = ['Easy', 'Medium', 'Hard'];
const ROMAN_NUMERALS = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];

const formatMarks = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  // Three places: Banking Mains marks go down to -0.375 and -0.125.
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
};

const getAvailableQuestionTypes = (patternValue, patternsList = []) => {
  if (isBoardPatternValue(patternValue)) {
    return BOARD_QUESTION_TYPES;
  }
  const matched = patternsList.find((p) => p.value === patternValue);
  const raw = (matched?.label || matched?.name || patternValue || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (raw.includes('jeemains') || raw.includes('neet')) {
    return QUESTION_TYPES.filter((type) => type.value === 'single_mcq');
  }
  return QUESTION_TYPES;
};

const DEFAULT_COL_A_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const DEFAULT_COL_B_LETTERS = ['P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W'];

const getMatrixColumns = (question) => {
  if (!question) return { hasColumns: false, colA: [], colB: [] };
  const colA =
    question.column_a ||
    question.columnA ||
    question.list_1 ||
    question.list1 ||
    question.column_1 ||
    question.list_a ||
    question.listA;
  const colB =
    question.column_b ||
    question.columnB ||
    question.list_2 ||
    question.list2 ||
    question.column_2 ||
    question.list_b ||
    question.listB;
  const hasColumns = Array.isArray(colA) && colA.length > 0 && Array.isArray(colB) && colB.length > 0;
  return {
    hasColumns,
    colA: Array.isArray(colA) ? colA : [],
    colB: Array.isArray(colB) ? colB : [],
  };
};

const parseMatrixItem = (item, defaultLetter) => {
  if (item === null || item === undefined) {
    return {
      label: defaultLetter ? `(${defaultLetter})` : '',
      text: ''
    };
  }
  const str = typeof item === 'object' ? JSON.stringify(item) : String(item).trim();
  // Match patterns like "(A)", "A.", "A)", "(1)", "1.", "(P)", "P." at start
  const match = str.match(/^(\([A-Za-z0-9]+\)|[A-Za-z0-9]+[.)])\s+(.*)$/s);
  if (match) {
    return {
      label: match[1],
      text: match[2]
    };
  }
  const matchNoSpace = str.match(/^(\([A-Za-z0-9]+\))(.*)$/s);
  if (matchNoSpace) {
    return {
      label: matchNoSpace[1],
      text: matchNoSpace[2].trim()
    };
  }
  return {
    label: defaultLetter ? `(${defaultLetter})` : '',
    text: str
  };
};

const getCorrectMappingPairs = (question) => {
  if (!question) return null;
  let mapping =
    question.correct_mapping ||
    question.correctMapping ||
    question.mapping ||
    question.matrix_mapping;

  if (!mapping && question.correct_answer && typeof question.correct_answer === 'object') {
    mapping = question.correct_answer;
  }
  if (!mapping && question.answer && typeof question.answer === 'object') {
    mapping = question.answer;
  }

  // If mapping is a string that looks like JSON
  if (typeof mapping === 'string') {
    const trimmed = mapping.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        mapping = JSON.parse(trimmed);
      } catch (e) {
        // ignore parse error
      }
    }
  }

  // If still not an object/array, check if string like "A-P, B-Q"
  if (typeof mapping === 'string' || (!mapping && typeof question.correct_answer === 'string')) {
    const str = typeof mapping === 'string' ? mapping : question.correct_answer;
    if (str && /[A-D]\s*[-–—→>:]+\s*[P-S]/i.test(str)) {
      const regex = /([A-Za-z0-9]+)\s*[-–—→>:]+\s*([A-Za-z0-9, ]+?)(?:;|\n|$)/g;
      const pairs = [];
      let m;
      while ((m = regex.exec(str)) !== null) {
        pairs.push({
          left: m[1].trim().toUpperCase(),
          right: m[2].trim().toUpperCase()
        });
      }
      if (pairs.length > 0) return pairs;
    }
  }

  if (!mapping) return null;

  if (Array.isArray(mapping)) {
    const pairs = [];
    mapping.forEach((item) => {
      if (Array.isArray(item) && item.length >= 2) {
        pairs.push({
          left: String(item[0]).trim().toUpperCase(),
          right: Array.isArray(item[1]) ? item[1].map(String).join(', ') : String(item[1]).trim()
        });
      } else if (typeof item === 'object' && item !== null) {
        const left = item.from || item.left || item.col_a || item.column_a || item.a || Object.keys(item)[0];
        const right = item.to || item.right || item.col_b || item.column_b || item.b || Object.values(item)[0];
        if (left !== undefined && right !== undefined) {
          pairs.push({
            left: String(left).trim().toUpperCase(),
            right: Array.isArray(right) ? right.map(String).join(', ') : String(right).trim()
          });
        }
      }
    });
    return pairs.length > 0 ? pairs : null;
  }

  if (typeof mapping === 'object' && mapping !== null) {
    const pairs = [];
    Object.keys(mapping).forEach((key) => {
      const val = mapping[key];
      const rightVal = Array.isArray(val) ? val.map(String).join(', ') : String(val ?? '').trim();
      pairs.push({
        left: String(key).trim().toUpperCase(),
        right: rightVal
      });
    });
    return pairs.length > 0 ? pairs : null;
  }

  return null;
};

const isIntegerQuestion = (question, selectedTypes = []) => {
  const declared = String(question?.type ?? question?.question_type ?? '').toLowerCase();
  if (declared.includes('matrix')) {
    return false;
  }
  if (question?.correct_mapping || question?.correctMapping || getMatrixColumns(question).hasColumns) {
    return false;
  }
  const chosen = (selectedTypes || []).filter(Boolean);
  if (chosen.length === 1 && (chosen[0] === 'integer_type' || chosen[0] === 'numeric' || chosen[0] === 'Numeric')) {
    return true;
  }
  if (declared.includes('integer') || declared.includes('numeric')) {
    return true;
  }
  if (chosen.includes('integer_type')) {
    if (!Array.isArray(question?.options) || question.options.length === 0) {
      return true;
    }
    const ans = String(question?.correct_answer ?? question?.answer ?? '').trim();
    if (/^-?\d+(\.\d+)?$/.test(ans)) {
      return true;
    }
  }
  return false;
};

const resolveQuestionType = (question, selectedTypes = []) => {
  const raw = String(question?.question_type ?? question?.type ?? '').toLowerCase().trim();
  if (raw) {
    if (raw.includes('matrix') || raw.includes('match')) return 'matrix_match';
    if (raw.includes('multi') || raw === 'msq') return 'multi_correct_mcq';
    if (raw.includes('integer') || raw.includes('numeric')) return 'integer_type';
    if (raw.includes('single') || raw.includes('mcq') || raw === 'scq') return 'single_mcq';
    return raw;
  }
  // If not declared, infer from question structure
  if (getMatrixColumns(question).hasColumns || question?.correct_mapping) {
    return 'matrix_match';
  }
  if (!Array.isArray(question?.options) || question.options.length === 0) {
    return 'integer_type';
  }
  const chosen = (selectedTypes || []).filter(Boolean);
  if (chosen.length === 1) {
    return chosen[0];
  }
  return 'single_mcq';
};

const getQuestionMarks = (question, test, selectedTypes = []) => {
  // 1. Direct marks on question object
  if (question?.marks !== undefined && question?.marks !== null && question?.marks !== '') {
    return question.marks;
  }
  if (question?.marking_scheme?.correct !== undefined && question?.marking_scheme?.correct !== null && question?.marking_scheme?.correct !== '') {
    return question.marking_scheme.correct;
  }

  // 2. marking_scheme_by_type on test or _metadata or question
  const byType =
    test?.marking_scheme_by_type ||
    test?._metadata?.marking_scheme_by_type ||
    question?.marking_scheme_by_type;

  if (byType && typeof byType === 'object') {
    const resolvedType = resolveQuestionType(question, selectedTypes);
    const rawType = String(question?.question_type ?? question?.type ?? '').toLowerCase().trim();

    // Check direct keys
    const candidateKeys = [resolvedType, rawType].filter(Boolean);
    for (const key of candidateKeys) {
      if (byType[key]?.correct !== undefined && byType[key]?.correct !== null && byType[key]?.correct !== '') {
        return byType[key].correct;
      }
    }

    // Normalized / alias matching
    const normResolved = String(resolvedType).toLowerCase().replace(/[^a-z0-9]/g, '');
    const normRaw = String(rawType).toLowerCase().replace(/[^a-z0-9]/g, '');

    for (const [key, val] of Object.entries(byType)) {
      if (!val || val.correct === undefined || val.correct === null || val.correct === '') continue;
      const normKey = String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
      if (normKey === normResolved || normKey === normRaw) {
        return val.correct;
      }
      // single mcq aliases
      const isSingleKey = normKey === 'singlemcq' || normKey === 'mcq' || normKey === 'single' || normKey === 'singlechoice' || normKey === 'scq';
      const isSingleQ = normResolved === 'singlemcq' || normRaw === 'singlemcq' || normRaw === 'mcq' || normRaw === 'single' || normRaw === 'scq' || !normRaw;
      if (isSingleKey && isSingleQ) {
        return val.correct;
      }
      // multi mcq aliases
      const isMultiKey = normKey === 'multicorrectmcq' || normKey === 'multimcq' || normKey === 'msq' || normKey === 'multiplechoice' || normKey === 'multi';
      const isMultiQ = normResolved === 'multicorrectmcq' || normRaw === 'multicorrectmcq' || normRaw === 'msq';
      if (isMultiKey && isMultiQ) {
        return val.correct;
      }
      // integer aliases
      const isIntKey = normKey === 'integertype' || normKey === 'integer' || normKey === 'numeric' || normKey === 'numerical';
      const isIntQ = normResolved === 'integertype' || normRaw === 'integertype' || normRaw === 'numeric';
      if (isIntKey && isIntQ) {
        return val.correct;
      }
      // matrix match aliases
      const isMatrixKey = normKey === 'matrixmatch' || normKey === 'matrix' || normKey === 'match';
      const isMatrixQ = normResolved === 'matrixmatch' || normRaw === 'matrixmatch';
      if (isMatrixKey && isMatrixQ) {
        return val.correct;
      }
    }

    // If only one marking scheme is defined in marking_scheme_by_type, apply it
    const validEntries = Object.values(byType).filter((v) => v && v.correct !== undefined && v.correct !== null && v.correct !== '');
    if (validEntries.length === 1) {
      return validEntries[0].correct;
    }
  }

  // 3. Fallback to top-level marking_scheme.correct
  const topCorrect = test?.marking_scheme?.correct ?? test?._metadata?.marking_scheme?.correct;
  if (topCorrect !== undefined && topCorrect !== null && topCorrect !== '') {
    return topCorrect;
  }

  return null;
};

const getQuestionNegativeMarks = (question, test, selectedTypes = []) => {
  // 1. Direct negative marks on question object
  const qDirect =
    question?.negative_marks ??
    question?.negativeMarks ??
    question?.incorrect_marks ??
    question?.incorrect ??
    question?.marking_scheme?.incorrect ??
    question?.marking_scheme?.negative ??
    question?.marking_scheme?.negative_marks;

  if (qDirect !== undefined && qDirect !== null && qDirect !== '') {
    return qDirect;
  }

  // 2. marking_scheme_by_type on test, _metadata, or question
  const byType =
    test?.marking_scheme_by_type ||
    test?._metadata?.marking_scheme_by_type ||
    question?.marking_scheme_by_type;

  if (byType && typeof byType === 'object') {
    const resolvedType = resolveQuestionType(question, selectedTypes);
    const rawType = String(question?.question_type ?? question?.type ?? '').toLowerCase().trim();

    const getIncorrectFromVal = (val) => {
      if (!val) return null;
      const v =
        val.incorrect ??
        val.negative ??
        val.negative_marking ??
        val.negative_marks ??
        val.penalty;
      return v !== undefined && v !== null && v !== '' ? v : null;
    };

    // Check direct keys
    const candidateKeys = [resolvedType, rawType].filter(Boolean);
    for (const key of candidateKeys) {
      const inc = getIncorrectFromVal(byType[key]);
      if (inc !== null) return inc;
    }

    // Normalized / alias matching
    const normResolved = String(resolvedType).toLowerCase().replace(/[^a-z0-9]/g, '');
    const normRaw = String(rawType).toLowerCase().replace(/[^a-z0-9]/g, '');

    for (const [key, val] of Object.entries(byType)) {
      const inc = getIncorrectFromVal(val);
      if (inc === null) continue;
      const normKey = String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
      if (normKey === normResolved || normKey === normRaw) {
        return inc;
      }
      // single mcq aliases
      const isSingleKey = normKey === 'singlemcq' || normKey === 'mcq' || normKey === 'single' || normKey === 'singlechoice' || normKey === 'scq';
      const isSingleQ = normResolved === 'singlemcq' || normRaw === 'singlemcq' || normRaw === 'mcq' || normRaw === 'single' || normRaw === 'scq' || !normRaw;
      if (isSingleKey && isSingleQ) {
        return inc;
      }
      // multi mcq aliases
      const isMultiKey = normKey === 'multicorrectmcq' || normKey === 'multimcq' || normKey === 'msq' || normKey === 'multiplechoice' || normKey === 'multi';
      const isMultiQ = normResolved === 'multicorrectmcq' || normRaw === 'multicorrectmcq' || normRaw === 'msq';
      if (isMultiKey && isMultiQ) {
        return inc;
      }
      // integer aliases
      const isIntKey = normKey === 'integertype' || normKey === 'integer' || normKey === 'numeric' || normKey === 'numerical';
      const isIntQ = normResolved === 'integertype' || normRaw === 'integertype' || normRaw === 'numeric';
      if (isIntKey && isIntQ) {
        return inc;
      }
      // matrix match aliases
      const isMatrixKey = normKey === 'matrixmatch' || normKey === 'matrix' || normKey === 'match';
      const isMatrixQ = normResolved === 'matrixmatch' || normRaw === 'matrixmatch';
      if (isMatrixKey && isMatrixQ) {
        return inc;
      }
    }

    // If only one scheme in marking_scheme_by_type
    const validEntries = Object.values(byType)
      .map(getIncorrectFromVal)
      .filter((v) => v !== null);
    if (validEntries.length === 1) {
      return validEntries[0];
    }
  }

  // 3. Fallback to top-level marking_scheme
  const topIncorrect =
    test?.marking_scheme?.incorrect ??
    test?.marking_scheme?.negative ??
    test?.marking_scheme?.negative_marks ??
    test?._metadata?.marking_scheme?.incorrect;

  if (topIncorrect !== undefined && topIncorrect !== null && topIncorrect !== '') {
    return topIncorrect;
  }

  return null;
};

// Backend sends one "<Subject> / <Chapter>: X of Y questions generated" segment
// per short chapter, joined by "; ". Collapse that into a one-line summary.
const summarizeQualityWarning = (warning) => {
  if (!warning || typeof warning !== 'string') return '';
  const segments = warning.split(';').map((s) => s.trim()).filter(Boolean);
  let got = 0;
  let wanted = 0;
  for (const seg of segments) {
    const m = seg.match(/(\d+)\s+of\s+(\d+)\s+questions?\s+generated/i);
    if (!m) return warning.length > 140 ? `${warning.slice(0, 137)}...` : warning;
    got += Number(m[1]);
    wanted += Number(m[2]);
  }
  const n = segments.length;
  return `${n} chapter${n > 1 ? 's' : ''} came up short: ${got} of ${wanted} questions generated.`;
};

const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const MAX_QUESTIONS_SINGLE = 60;
const MAX_QUESTIONS_MULTI = 60;

const toOptions = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw && raw.data) ? raw.data : [];
  return list
    .map((item) => {
      if (item === null || item === undefined) return null;
      if (typeof item === 'object') {
        const value = item.id ?? item.value ?? item.name;
        if (value === null || value === undefined) return null;
        return { value: String(value), label: String(item.name ?? item.label ?? value) };
      }
      return { value: String(item), label: String(item) };
    })
    .filter(Boolean);
};

// Chapter rows come back subject by subject tagged with `subject`.
// Key rows by unique id because chapter names repeat across subjects
// (e.g., "Thermodynamics" in Physics and Chemistry).
const toChapterOptions = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw && raw.data) ? raw.data : [];
  return list
    .map((item, index) => {
      if (item === null || item === undefined) return null;
      if (typeof item === 'object') {
        const name = item.name ?? item.id;
        const id = item.id ?? item.value ?? name;
        if (name === null || name === undefined) return null;
        return {
          id: String(id),
          value: String(id),
          name: String(name),
          label: String(name),
          key: String(id),
          subject: item.subject ? String(item.subject) : '',
          class_: item.class_ ?? item.class ?? '',
          order: typeof item.order === 'number' ? item.order : index
        };
      }
      return {
        id: String(item),
        value: String(item),
        name: String(item),
        label: String(item),
        key: String(item),
        subject: '',
        class_: '',
        order: index
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.subject !== b.subject) return 0;
      return String(a.class_).localeCompare(String(b.class_)) || a.order - b.order;
    })
    // Deduplicate identical chapter names only within the same subject (e.g. class=Both duplicates)
    .filter(
      (chapter, index, all) =>
        all.findIndex((other) => other.subject === chapter.subject && other.name === chapter.name) === index
    );
};

const groupChaptersByClass = (chapterList) => {
  const map = new Map();
  chapterList.forEach((c) => {
    const cls = c.class_ || 'Other';
    if (!map.has(cls)) {
      map.set(cls, []);
    }
    map.get(cls).push(c);
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => String(a).localeCompare(String(b), undefined, { numeric: true }))
    .map(([clsKey, chs]) => ({
      classKey: clsKey,
      className: clsKey.toLowerCase().startsWith('class') ? clsKey : `Class ${clsKey}`,
      chapters: chs
    }));
};

const blockInvalidNumberKeys = (e) => {
  if (['e', 'E', '-', '+'].includes(e.key)) {
    e.preventDefault();
  }
};

const blockInvalidNumberPaste = (e) => {
  const paste = e.clipboardData?.getData('text') || '';
  if (/[eE\-+]/.test(paste)) {
    e.preventDefault();
    const clean = paste.replace(/[eE\-+]/g, '');
    document.execCommand('insertText', false, clean);
  }
};

const handleWeightKeyDown = (e) => {
  blockInvalidNumberKeys(e);
  const target = e.target;
  const isDigitOrDot = /^[0-9.]$/.test(e.key);
  if (isDigitOrDot && target && typeof target.selectionStart === 'number') {
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const currentVal = target.value || '';
    const nextVal = currentVal.slice(0, start) + e.key + currentVal.slice(end);
    const parsed = parseFloat(nextVal);
    if (!isNaN(parsed) && parsed > 100) {
      e.preventDefault();
    }
  }
};

const TestGenerationTab = ({ startAiTestHandoff, handoffLoading }) => {
  const history = useHistory();
  const location = useLocation();
  // Coming back from /create-test: pick up where the teacher left off.
  const [restored] = useState(() => getGenerationSnapshot());
  const [health, setHealth] = useState({ state: 'checking', data: null });
  const [patterns, setPatterns] = useState([]);
  const [classes, setClasses] = useState(restored?.classes || []);
  const [subjects, setSubjects] = useState(restored?.subjects || []);
  const [chapters, setChapters] = useState(restored?.chapters || []);
  const [selection, setSelection] = useState(() => ({
    title: '',
    pattern: '',
    class: '',
    subject: [],
    chapters: [],
    n_questions: '',
    difficulty: '',
    question_types: [],
    total_marks: '',
    duration_mins: '',
    // Board: 'custom' (default) or 'full' (the CBSE blueprint).
    // Banking: 'full' (the whole mock), 'sectional' (one full section) or 'custom'.
    paper_mode: 'custom',
    // 'single' sends `difficulty`; 'mix' sends `difficulty_weightage`.
    difficulty_mode: 'single',
    difficulty_mix: { Easy: '', Medium: '', Hard: '' },
    ...(restored?.selection || {})
  }));
  // Percentages keyed by unique chapter ID.
  const [weightage, setWeightage] = useState(restored?.weightage || {});
  // Optional topic scope per chapter ID (not for Banking): { topics: [...], focus: '...' }.
  const [topicScope, setTopicScope] = useState(restored?.topicScope || {});
  // Chapters whose topic-scope fields are open while still empty.
  const [openScopes, setOpenScopes] = useState({});
  const [loading, setLoading] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedTest, setGeneratedTest] = useState(restored?.generatedTest || null);
  // Board sets holding both kinds: which questions become the test.
  const [testKind, setTestKind] = useState(restored?.testKind || 'Mixed');
  // Marks for written questions the generator sent without marks.
  const [subjectiveMarks, setSubjectiveMarks] = useState(restored?.subjectiveMarks || '');
  const [lastPayload, setLastPayload] = useState(restored?.lastPayload || null);
  const [lastGeneratedPayloadStr, setLastGeneratedPayloadStr] = useState(restored?.lastGeneratedPayloadStr || '');
  const [elapsed, setElapsed] = useState(0);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  // 'paper' | 'solutions' while that PDF is being built.
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isBulkRegenerating, setIsBulkRegenerating] = useState(false);
  const [regeneratingIds, setRegeneratingIds] = useState([]);
  const [recentlyRegeneratedIds, setRecentlyRegeneratedIds] = useState([]);
  const [activeSectionFilter, setActiveSectionFilter] = useState('ALL');
  // Banking: the stage's full paper (sections, their counts, time and topics), from /api/dropdowns/blueprint
  const [blueprint, setBlueprint] = useState(null);

  const availableQuestionTypes = getAvailableQuestionTypes(selection.pattern, patterns);
  const selectedSubjects = useMemo(() => {
    return Array.isArray(selection.subject)
      ? selection.subject.filter(Boolean)
      : selection.subject ? [selection.subject] : [];
  }, [selection.subject]);

  const isMultiSubject = selectedSubjects.length > 1;
  const isBoardPattern = useMemo(() => isBoardPatternValue(selection.pattern), [selection.pattern]);
  const isFullBoardPaper = isBoardPattern && selection.paper_mode === 'full';
  const fullBoardBlueprint = getFullBoardBlueprint(selectedSubjects[0]);
  const {
    isBanking, isBankingFull, isBankingSectional, isBankingBlueprintPaper, terms, maxQuestions
  } = bankingFormState(selection, isMultiSubject);
  // Topic name -> its blueprint entry ('set' or 'single'), across the stage's sections.
  const blueprintTopics = useMemo(() => {
    const map = new Map();
    (blueprint?.sections || []).forEach((sec) => (sec.topics || []).forEach((t) => map.set(`${sec.subject}::${t.name}`, t)));
    return map;
  }, [blueprint]);
  const isSetTopic = (chapter) => blueprintTopics.get(`${chapter.subject}::${chapter.name}`)?.form === 'set';
  // Fewest questions the chosen topics can take: one each, or a whole set for a set topic.
  const minQuestionsFor = (subjChapters) =>
    subjChapters.reduce((sum, c) => sum + (isBanking && isSetTopic(c) ? MIN_SET_QUESTIONS : 1), 0);
  const isDifficultyMix = selection.difficulty_mode === 'mix';
  const difficultyMixTotal = DIFFICULTY_MIX_LEVELS.reduce(
    (sum, level) => sum + (parseFloat(selection.difficulty_mix?.[level]) || 0),
    0
  );
  const isDifficultyMixBalanced = Math.abs(difficultyMixTotal - 100) < 0.05;

  const showChapterClass =
    selection.class === 'Both' &&
    new Set(chapters.map((c) => c.class_).filter(Boolean)).size > 1;

  const chaptersBySubject = useMemo(() => {
    const groups = [];
    selectedSubjects.forEach((subjVal) => {
      const subjObj = subjects.find((s) => s.value === subjVal);
      const subjectName = subjObj?.label || subjVal;
      const chs = chapters.filter((c) => (c.subject || c.subjectValue) === subjVal);
      if (chs.length > 0) {
        groups.push({
          subjectValue: subjVal,
          subjectName: subjectName,
          chapters: chs
        });
      }
    });
    return groups;
  }, [selectedSubjects, subjects, chapters]);

  // Elapsed timer during generation
  useEffect(() => {
    if (!isGenerating) return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(id);
  }, [isGenerating]);

  const fetchOptions = useCallback(async (path, field, mapper) => {
    setLoading((prev) => ({ ...prev, [field]: true }));
    try {
      const response = await fetch(`${API_BASE}${path}`);
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      return mapper(await response.json());
    } catch (err) {
      console.error(`Failed to load ${field}:`, err);
      setError(`Could not load ${field}: ${err.message}`);
      return [];
    } finally {
      setLoading((prev) => ({ ...prev, [field]: false }));
    }
  }, []);

  const checkHealth = useCallback(async () => {
    setHealth({ state: 'checking', data: null });
    try {
      const response = await fetch(`${API_BASE}/api/health`);
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      const data = await response.json();
      setHealth({ state: data.status === 'ok' ? 'ok' : 'down', data });
    } catch (err) {
      console.error('Health check failed:', err);
      setHealth({ state: 'down', data: null });
    }
  }, []);

  useEffect(() => { checkHealth(); }, [checkHealth]);

  // The "Generated Test" bar sticks directly under the fixed page header. Its
  // height depends on zoom and screen scaling, so measure where it really ends
  // (relative to the element that scrolls) instead of hard-coding it.
  const pageRef = useRef(null);
  const [stickyTop, setStickyTop] = useState(44);
  useEffect(() => {
    const root = pageRef.current;
    const header = root && root.querySelector('.headerStyle');
    if (!header) return undefined;
    const findScrollParent = (el) => {
      let node = el && el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
      }
      return null;
    };
    const measure = () => {
      const scroller = findScrollParent(root);
      const scrollerTop = scroller ? scroller.getBoundingClientRect().top : 0;
      // `top` on a sticky element is measured from inside the scroll container's
      // padding, and #mainBody has 20px of it on this route - without this the
      // bar sticks 20px below the header and content shows through the gap.
      const scrollerPadding = scroller ? parseFloat(window.getComputedStyle(scroller).paddingTop) || 0 : 0;
      const next = Math.max(0, Math.round(header.getBoundingClientRect().bottom - scrollerTop - scrollerPadding));
      setStickyTop((prev) => (prev === next ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(header);
    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, []);

  // GET /api/dropdowns/patterns
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const options = await fetchOptions('/api/dropdowns/patterns', 'patterns', toOptions);
      if (!cancelled) setPatterns(options);
    })();
    return () => { cancelled = true; };
  }, [fetchOptions]);

  // GET /api/dropdowns/classes and /api/dropdowns/subjects
  useEffect(() => {
    if (!selection.pattern) {
      setClasses([]);
      setSubjects([]);
      return undefined;
    }
    let cancelled = false;
    const pattern = encodeURIComponent(selection.pattern);
    // Banking lists each stage's own sections, so those load once a stage is picked (below).
    const banking = isBankingPatternValue(selection.pattern);
    (async () => {
      const [classOptions, subjectOptions] = await Promise.all([
        fetchOptions(`/api/dropdowns/classes?pattern=${pattern}`, 'classes', toOptions),
        banking ? Promise.resolve(null) : fetchOptions(`/api/dropdowns/subjects?pattern=${pattern}`, 'subjects', toOptions)
      ]);
      if (cancelled) return;
      setClasses(classOptions);
      if (subjectOptions) setSubjects(subjectOptions);
    })();
    return () => { cancelled = true; };
  }, [selection.pattern, fetchOptions]);

  // Banking: GET /api/dropdowns/subjects?pattern=Banking&class=<stage>, and that stage's blueprint
  useEffect(() => {
    if (!isBankingPatternValue(selection.pattern)) {
      setBlueprint(null);
      return undefined;
    }
    if (!selection.class) {
      setSubjects([]);
      setBlueprint(null);
      return undefined;
    }
    let cancelled = false;
    const query = new URLSearchParams({ pattern: selection.pattern, class: selection.class });
    (async () => {
      const [subjectOptions, stageBlueprint] = await Promise.all([
        fetchOptions(`/api/dropdowns/subjects?${query.toString()}`, 'subjects', toOptions),
        fetchOptions(`/api/dropdowns/blueprint?class=${encodeURIComponent(selection.class)}`, 'blueprint', (json) => json)
      ]);
      if (cancelled) return;
      setSubjects(subjectOptions);
      setBlueprint(stageBlueprint && Array.isArray(stageBlueprint.sections) ? stageBlueprint : null);
    })();
    return () => { cancelled = true; };
  }, [selection.pattern, selection.class, fetchOptions]);

  // GET /api/dropdowns/chapters?pattern=...&class=...&subject=Physics,Chemistry
  useEffect(() => {
    const selectedSubs = Array.isArray(selection.subject)
      ? selection.subject.filter(Boolean)
      : selection.subject ? [selection.subject] : [];
    if (!selection.pattern || !selection.class || selectedSubs.length === 0) {
      setChapters([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading((prev) => ({ ...prev, chapters: true }));
      try {
        const query = new URLSearchParams({
          pattern: selection.pattern,
          class: selection.class,
          subject: selectedSubs.join(',')
        });
        const response = await fetch(`${API_BASE}/api/dropdowns/chapters?${query.toString()}`);
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        const json = await response.json();
        const options = toChapterOptions(json);
        if (!cancelled) {
          setChapters(options);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load chapters:', err);
          setError(`Could not load chapters: ${err.message}`);
          setChapters([]);
        }
      } finally {
        if (!cancelled) {
          setLoading((prev) => ({ ...prev, chapters: false }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selection.pattern, selection.class, selection.subject]);

  // Clean up selected chapters if they are no longer in loaded chapters
  useEffect(() => {
    if (chapters.length === 0) {
      if (selection.chapters.length > 0) {
        setSelection((prev) => ({ ...prev, chapters: [] }));
        setWeightage({});
      }
      return;
    }
    const validIds = new Set(chapters.map((c) => c.id));
    const filtered = selection.chapters.filter((id) => validIds.has(id));
    if (filtered.length !== selection.chapters.length) {
      setSelection((prev) => ({ ...prev, chapters: filtered }));
      setWeightage((prev) => {
        const next = {};
        filtered.forEach((id) => {
          if (prev[id] !== undefined) next[id] = prev[id];
        });
        return next;
      });
    }
  }, [chapters]);

  const handlePatternChange = (e) => {
    const value = e.target.value;
    setError('');
    setGeneratedTest(null);
    setWeightage({});
    const allowedTypeValues = getAvailableQuestionTypes(value, patterns).map((t) => t.value);
    setSelection((prev) => {
      const filteredTypes = prev.question_types.filter((t) => allowedTypeValues.includes(t));
      return {
        ...prev,
        pattern: value,
        class: '',
        subject: [],
        chapters: [],
        question_types: filteredTypes,
        // A Banking paper starts as the full mock, the exam as it is sat.
        paper_mode: isBankingPatternValue(value) ? 'full' : 'custom'
      };
    });
  };

  const handlePaperModeChange = (mode) => {
    setError('');
    if (isBanking && mode !== 'custom') {
      // A full mock takes no section; a full section takes exactly one. Neither takes topics.
      setWeightage({});
      setSelection((prev) => ({
        ...prev,
        paper_mode: mode,
        subject: mode === 'sectional' ? selectedSubjects.slice(0, 1) : [],
        chapters: []
      }));
      return;
    }
    setSelection((prev) => ({ ...prev, paper_mode: mode }));
  };

  const handleDifficultyModeChange = (mode) => {
    setError('');
    setSelection((prev) => ({ ...prev, difficulty_mode: mode }));
  };

  const handleDifficultyMixChange = (level, value) => {
    setError('');
    const cleanValue = String(value ?? '').replace(/[eE\-+]/g, '');
    if (cleanValue !== '' && (/^0\d/.test(cleanValue) || parseFloat(cleanValue) > 100)) return;
    setSelection((prev) => ({
      ...prev,
      difficulty_mix: { ...(prev.difficulty_mix || {}), [level]: cleanValue }
    }));
  };

  const handleClassChange = (e) => {
    const value = e.target.value;
    setError('');
    setWeightage({});
    setSelection((prev) => ({
      ...prev,
      class: value,
      // Each Banking stage has its own sections, so a new stage starts with none chosen.
      subject: value && !isBankingPatternValue(prev.pattern) ? prev.subject : [],
      chapters: []
    }));
  };

  const handleSubjectChange = (e) => {
    const raw = e.target.value;
    const value = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    setError('');
    setSelection((prev) => {
      // Retain chapters belonging only to remaining subjects
      const remainingChapters = prev.chapters.filter((chId) => {
        const ch = chapters.find((c) => c.id === chId);
        return ch ? value.includes(ch.subject) : false;
      });
      return { ...prev, subject: value, chapters: remainingChapters };
    });
  };

  const handleSelectAllSubjects = () => {
    setError('');
    const allValues = subjects.map((s) => s.value);
    setSelection((prev) => ({ ...prev, subject: allValues }));
  };

  const handleClearSubjects = () => {
    setError('');
    setSelection((prev) => ({ ...prev, subject: [], chapters: [] }));
    setWeightage({});
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setError('');
    const cleanValue = ['n_questions', 'total_marks', 'duration_mins'].includes(name)
      ? value.replace(/[eE\-+]/g, '')
      : value;
    setSelection((prev) => ({ ...prev, [name]: cleanValue }));
  };

  // Helper: distribute weightage evenly across a subject's selected chapters
  const distributeSubjectEvenly = (subj, currentChapters) => {
    const subjChs = chapters.filter(
      (c) => (c.subject || selection.subject) === subj && currentChapters.includes(c.id)
    );
    const n = subjChs.length;
    if (n === 0) return {};
    const share = Math.floor((100 / n) * 100) / 100;
    const result = {};
    subjChs.forEach((c, index) => {
      result[c.id] = index === n - 1 ? Number((100 - share * (n - 1)).toFixed(2)) : share;
    });
    return result;
  };

  const toggleChapter = (chapterId) => {
    setError('');
    const isSelected = selection.chapters.includes(chapterId);
    const nextChapters = isSelected
      ? selection.chapters.filter((id) => id !== chapterId)
      : [...selection.chapters, chapterId];

    setSelection((prev) => ({ ...prev, chapters: nextChapters }));

    // Find the subject of this chapter
    const ch = chapters.find((c) => c.id === chapterId);
    const subj = ch?.subject || (selectedSubjects.length === 1 ? selectedSubjects[0] : '');

    if (subj) {
      const newSubjWeights = distributeSubjectEvenly(subj, nextChapters);
      setWeightage((prev) => {
        const next = { ...prev };
        // Clear all weights for this subject's chapters
        chapters
          .filter((c) => (c.subject || selection.subject) === subj)
          .forEach((c) => { delete next[c.id]; });
        // Assign new even weights
        Object.assign(next, newSubjWeights);
        return next;
      });
    }
  };

  const handleSelectAllSubjectChapters = (subj) => {
    setError('');
    const subjChs = chapters.filter((c) => (c.subject || selection.subject) === subj);
    const subjIds = subjChs.map((c) => c.id);
    const nextChapters = Array.from(new Set([...selection.chapters, ...subjIds]));
    setSelection((prev) => ({ ...prev, chapters: nextChapters }));

    const newWeights = distributeSubjectEvenly(subj, nextChapters);
    setWeightage((prev) => ({ ...prev, ...newWeights }));
  };

  const handleClearSubjectChapters = (subj) => {
    setError('');
    const subjChs = chapters.filter((c) => (c.subject || selection.subject) === subj);
    const subjIds = new Set(subjChs.map((c) => c.id));
    const nextChapters = selection.chapters.filter((id) => !subjIds.has(id));
    setSelection((prev) => ({ ...prev, chapters: nextChapters }));
    setWeightage((prev) => {
      const next = { ...prev };
      subjIds.forEach((id) => { delete next[id]; });
      return next;
    });
  };

  const handleSelectAllAllChapters = () => {
    setError('');
    const allIds = chapters.map((c) => c.id);
    setSelection((prev) => ({ ...prev, chapters: allIds }));

    const nextWeights = {};
    selectedSubjects.forEach((subj) => {
      Object.assign(nextWeights, distributeSubjectEvenly(subj, allIds));
    });
    setWeightage(nextWeights);
  };

  const handleClearAllChapters = () => {
    setError('');
    setSelection((prev) => ({ ...prev, chapters: [] }));
    setWeightage({});
  };

  const toggleQuestionType = (value) => {
    setError('');
    setSelection((prev) => ({
      ...prev,
      question_types: prev.question_types.includes(value)
        ? prev.question_types.filter((t) => t !== value)
        : [...prev.question_types, value]
    }));
  };

  const handleWeightChange = (chapterId, value) => {
    setError('');
    if (value === '') {
      setWeightage((prev) => ({ ...prev, [chapterId]: '' }));
      return;
    }
    const cleanValue = typeof value === 'string' ? value.replace(/[eE\-+]/g, '') : String(value);
    // Reject the keystroke outright if it leaves a leading zero ("05") or exceeds 100.
    // A lone "0" / "0." is kept so values like 0.5 can still be typed; blur clears it.
    if (/^0\d/.test(cleanValue) || parseFloat(cleanValue) > 100) return;
    setWeightage((prev) => ({ ...prev, [chapterId]: cleanValue }));
  };

  // Weights must be > 0, so a value left at 0 (or "0.") is cleared when the field loses focus.
  const handleWeightBlur = (chapterId) => {
    const raw = weightage[chapterId];
    if (raw !== undefined && raw !== '' && !(parseFloat(raw) > 0)) {
      setWeightage((prev) => ({ ...prev, [chapterId]: '' }));
    }
  };

  const getSubjectChapters = useCallback((subj) => {
    return chapters.filter(
      (c) => (c.subject || selection.subject) === subj && selection.chapters.includes(c.id)
    );
  }, [chapters, selection.subject, selection.chapters]);

  const getSubjectWeightTotal = useCallback((subj) => {
    const subjChs = getSubjectChapters(subj);
    return subjChs.reduce((sum, c) => sum + (parseFloat(weightage[c.id]) || 0), 0);
  }, [getSubjectChapters, weightage]);

  const isSubjectWeightBalanced = useCallback((subj) => {
    const total = getSubjectWeightTotal(subj);
    return Math.abs(total - 100) < 0.05;
  }, [getSubjectWeightTotal]);

  // Plain-language status shown under each weightage panel.
  const getWeightageMessage = (subj) => {
    const subjChs = getSubjectChapters(subj);
    const empty = subjChs.filter((c) => !(parseFloat(weightage[c.id]) > 0)).length;
    if (empty > 0) {
      return `${empty} chapter${empty > 1 ? 's need' : ' needs'} a percentage above 0`;
    }
    const total = getSubjectWeightTotal(subj);
    const diff = Math.abs(100 - total).toFixed(2);
    return total < 100
      ? `Add ${diff}% more so all chapters add up to 100%`
      : `Remove ${diff}% so all chapters add up to 100%`;
  };

  const handleDistributeSubjectEvenly = (subj) => {
    const newWeights = distributeSubjectEvenly(subj, selection.chapters);
    setWeightage((prev) => ({ ...prev, ...newWeights }));
  };

  // Normalise each subject's percentages into fractions summing to exactly 1.0.
  const buildSubjectWeightage = (subjChapters) => {
    const entries = subjChapters.map((chapter) => [
      chapter.name,
      parseFloat(weightage[chapter.id]) || 0
    ]);
    const total = entries.reduce((sum, [, value]) => sum + value, 0);
    if (total <= 0) return null;
    const keys = entries.map(([name]) => name);
    const head = entries
      .slice(0, -1)
      .map(([, value]) => Number((value / total).toFixed(4)));
    const headSum = head.reduce((sum, value) => sum + value, 0);
    const assemble = (lastValue) => {
      const result = {};
      keys.forEach((key, index) => {
        result[key] = index === keys.length - 1 ? lastValue : head[index];
      });
      return result;
    };
    const sumOf = (obj) => Object.values(obj).reduce((sum, value) => sum + value, 0);
    const rounded = assemble(Number((1 - headSum).toFixed(4)));
    if (sumOf(rounded) === 1) return rounded;
    const exact = assemble(1 - headSum);
    return sumOf(exact) === 1 ? exact : rounded;
  };

  // Request payload construction matching Push 5 specs:
  // When multiple subjects are selected, send chapters & weightage inside subjects[subject].
  // Do NOT send top-level subject, chapters or weightage.
  // Mix percentages -> fractions summing to exactly 1.0 (the last level takes the remainder).
  const buildDifficultyWeightage = () => {
    const values = DIFFICULTY_MIX_LEVELS.map((level) => parseFloat(selection.difficulty_mix?.[level]) || 0);
    const total = values.reduce((sum, value) => sum + value, 0);
    if (total <= 0) return null;
    const head = values.slice(0, -1).map((value) => Number((value / total).toFixed(4)));
    const last = Number((1 - head.reduce((sum, value) => sum + value, 0)).toFixed(4));
    const result = {};
    DIFFICULTY_MIX_LEVELS.forEach((level, index) => {
      result[level] = index === DIFFICULTY_MIX_LEVELS.length - 1 ? last : head[index];
    });
    return result;
  };

  const buildPayload = () => {
    const n = parseInt(selection.n_questions, 10);
    const basePayload = {
      pattern: selection.pattern,
      class: selection.class
    };
    if (isDifficultyMix) {
      basePayload.difficulty_weightage = buildDifficultyWeightage();
    } else {
      basePayload.difficulty = selection.difficulty;
    }
    if (isBankingBlueprintPaper) {
      // The blueprint fixes the paper: the whole mock, or one whole section.
      basePayload.paper_mode = 'full';
      if (isBankingSectional) basePayload.subject = selectedSubjects[0];
      return basePayload;
    }
    if (isFullBoardPaper) {
      // The CBSE blueprint fixes the question count and types.
      basePayload.paper_mode = 'full';
    } else {
      // Banking has no question types to pick: each topic has its own form.
      if (!isBanking) {
        basePayload.question_types = selection.question_types.filter((t) =>
          availableQuestionTypes.map((item) => item.value).includes(t)
        );
      }
      basePayload.n_questions = n;
    }

    if (selection.total_marks !== '') {
      basePayload.total_marks = parseInt(selection.total_marks, 10);
    }
    if (selection.duration_mins !== '') {
      basePayload.duration_mins = parseInt(selection.duration_mins, 10);
    }

    if (isMultiSubject) {
      const subjectsObj = {};
      selectedSubjects.forEach((subj) => {
        const subjChs = getSubjectChapters(subj);
        subjectsObj[subj] = {
          chapters: subjChs.map((c) => c.name),
          weightage: buildSubjectWeightage(subjChs)
        };
        const scope = buildTopicScope(subjChs);
        if (scope) subjectsObj[subj].topic_scope = scope;
      });
      return {
        ...basePayload,
        subjects: subjectsObj
      };
    }

    // Single subject shape (backward compatible)
    const singleSubj = selectedSubjects[0];
    const subjChs = getSubjectChapters(singleSubj);
    const scope = buildTopicScope(subjChs);
    return {
      ...basePayload,
      subject: singleSubj,
      chapters: subjChs.map((c) => c.name),
      weightage: buildSubjectWeightage(subjChs),
      ...(scope ? { topic_scope: scope } : {})
    };
  };

  // topic_scope for these chapters, keyed by chapter name: only chapters given topics or a focus.
  // Banking has no topic scope.
  function buildTopicScope(subjChapters) {
    if (isBanking) return null;
    const scope = {};
    subjChapters.forEach((chapter) => {
      const entry = topicScope[chapter.id] || {};
      const topics = (entry.topics || []).map((t) => String(t).trim()).filter(Boolean);
      const focus = String(entry.focus || '').trim();
      if (topics.length || focus) {
        scope[chapter.name] = { ...(topics.length ? { topics } : {}), ...(focus ? { focus } : {}) };
      }
    });
    return Object.keys(scope).length ? scope : null;
  }

  const updateTopicScope = (chapterId, patch) => {
    setError('');
    setTopicScope((prev) => ({ ...prev, [chapterId]: { topics: [], focus: '', ...prev[chapterId], ...patch } }));
  };

  const removeTopicScope = (chapterId) => {
    setTopicScope((prev) => {
      const next = { ...prev };
      delete next[chapterId];
      return next;
    });
    setOpenScopes((prev) => ({ ...prev, [chapterId]: false }));
  };

  const hasTopicScope = (chapterId) => {
    const entry = topicScope[chapterId];
    return !!entry && ((entry.topics || []).length > 0 || !!String(entry.focus || '').trim());
  };

  // "+ Topics" beside a chapter's weightage, until its topic fields are open.
  const renderTopicScopeToggle = (chapter) => {
    if (isBanking || openScopes[chapter.id] || hasTopicScope(chapter.id)) return null;
    return (
      <button
        type="button"
        className="link-btn topic-scope-toggle"
        onClick={() => setOpenScopes((prev) => ({ ...prev, [chapter.id]: true }))}
        disabled={weightageLocked}
      >
        + Topics
      </button>
    );
  };

  // Narrows a chapter to the named topics, with an optional focus line.
  const renderTopicScopePanel = (chapter) => {
    if (isBanking || (!openScopes[chapter.id] && !hasTopicScope(chapter.id))) return null;
    const entry = topicScope[chapter.id] || {};
    return (
      <div className="topic-scope-panel">
        <Select
          mode="tags"
          open={false}
          value={entry.topics || []}
          onChange={(value) => updateTopicScope(chapter.id, { topics: value })}
          tokenSeparators={[',']}
          placeholder="Topics in this chapter: type one, press Enter"
          disabled={weightageLocked}
          className="topic-scope-topics"
        />
        <Input
          value={entry.focus || ''}
          onChange={(e) => updateTopicScope(chapter.id, { focus: e.target.value })}
          placeholder="Focus (optional), e.g. satellites in circular orbit"
          maxLength={200}
          disabled={weightageLocked}
          className="topic-scope-focus"
        />
        <button
          type="button"
          className="link-btn"
          onClick={() => removeTopicScope(chapter.id)}
          disabled={weightageLocked}
        >
          Remove
        </button>
      </div>
    );
  };

  const validateDifficulty = () => {
    if (!isDifficultyMix) {
      return selection.difficulty ? '' : 'Difficulty is required';
    }
    if (!isDifficultyMixBalanced) {
      return `Difficulty mix must add up to 100% (currently ${formatMarks(difficultyMixTotal)}%)`;
    }
    return '';
  };

  const validate = () => {
    if (!selection.pattern) return 'Pattern is required';
    if (!selection.class) return `${terms.class} is required`;
    if (isBankingFull) return validateDifficulty();
    if (selectedSubjects.length === 0) return `${terms.subject} is required`;
    if (isBankingSectional) return validateDifficulty();

    for (const subj of selectedSubjects) {
      const subjChs = getSubjectChapters(subj);
      if (subjChs.length === 0) {
        return `Select at least one ${terms.chapter} for ${subj}`;
      }
      const subjTotal = getSubjectWeightTotal(subj);
      if (Math.abs(subjTotal - 100) >= 0.05) {
        return `${subj} weightage must total 100% (currently ${subjTotal.toFixed(2)}%)`;
      }
    }

    const difficultyError = validateDifficulty();
    if (isFullBoardPaper) {
      if (selection.chapters.length > fullBoardBlueprint.questions) {
        return `A full CBSE paper has ${fullBoardBlueprint.questions} questions, so select at most ${fullBoardBlueprint.questions} chapters.`;
      }
      return difficultyError;
    }

    const n = parseInt(selection.n_questions, 10);
    if (isNaN(n)) return 'Number of questions must be a valid number';
    const minQ = Math.max(1, selectedSubjects.length);
    const maxQ = maxQuestions;
    if (n < minQ) {
      return isMultiSubject
        ? `${selectedSubjects.length} subjects are selected but 'n_questions' is ${n}: each subject needs at least one question.`
        : `Number of questions must be at least 1`;
    }
    if (n > maxQ) {
      return `Number of questions must be between ${minQ} and ${maxQ}`;
    }

    // Question allocation capacity per subject (split evenly)
    if (isMultiSubject) {
      const S = selectedSubjects.length;
      const baseShare = Math.floor(n / S);
      const rem = n % S;
      for (let i = 0; i < S; i++) {
        const subj = selectedSubjects[i];
        const subjShare = baseShare + (i < rem ? 1 : 0);
        const subjChs = getSubjectChapters(subj);
        const need = minQuestionsFor(subjChs);
        if (subjShare < need) {
          return `${subj}: Cannot allocate ${subjShare} question(s) across ${subjChs.length} ${terms.chapter}(s), which need at least ${need}. Increase total questions or select fewer ${terms.chapters.toLowerCase()}.`;
        }
      }
    } else {
      const need = minQuestionsFor(getSubjectChapters(selectedSubjects[0]));
      if (selection.chapters.length > 0 && n < need) {
        return `Cannot allocate ${n} question(s) across ${selection.chapters.length} ${terms.chapter}(s), which need at least ${need}. Increase total questions or select fewer ${terms.chapters.toLowerCase()}.`;
      }
    }

    if (difficultyError) return difficultyError;
    const validQuestionTypes = selection.question_types.filter((t) =>
      availableQuestionTypes.map((item) => item.value).includes(t)
    );
    if (!isBanking && validQuestionTypes.length === 0) return 'Select at least one question type';
    if (selection.total_marks !== '' && parseInt(selection.total_marks, 10) < 1) {
      return 'Total marks must be at least 1';
    }
    if (selection.duration_mins !== '' && parseInt(selection.duration_mins, 10) < 1) {
      return 'Duration must be at least 1 minute';
    }
    return '';
  };

  // Inline message under "Number of Questions" when the count is too low for the
  // selected chapters (each chapter needs at least one question).
  const questionCountError = useMemo(() => {
    if (isFullBoardPaper || isBankingBlueprintPaper || selection.n_questions === '') return '';
    const n = parseInt(selection.n_questions, 10);
    if (isNaN(n)) return '';
    // Banking set topics take a whole set each, not one question.
    const setNote = isBanking ? ` (each Set ${terms.chapter} needs at least ${MIN_SET_QUESTIONS})` : '';
    if (isMultiSubject) {
      const S = selectedSubjects.length;
      if (n < S) return `Enter at least ${S} questions, one for each selected ${terms.subject.toLowerCase()}.`;
      for (let i = 0; i < S; i++) {
        const subj = selectedSubjects[i];
        const share = Math.floor(n / S) + (i < n % S ? 1 : 0);
        const subjChs = getSubjectChapters(subj);
        const need = minQuestionsFor(subjChs);
        if (share < need) {
          return `${subj} gets ${share} question${share > 1 ? 's' : ''} but its ${subjChs.length} selected ${terms.chapters.toLowerCase()} need at least ${need}${setNote}. Increase the number of questions or select fewer ${terms.chapters.toLowerCase()}.`;
        }
      }
      return '';
    }
    const chCount = selection.chapters.length;
    const need = minQuestionsFor(getSubjectChapters(selectedSubjects[0]));
    if (chCount > 0 && n < need) {
      return isBanking
        ? `Your ${chCount} selected ${terms.chapters.toLowerCase()} need at least ${need} questions${setNote}. Enter more, or select fewer ${terms.chapters.toLowerCase()}.`
        : `You selected ${chCount} chapters, so enter at least ${chCount} questions (one per chapter), or select fewer chapters.`;
    }
    return '';
  }, [selection.n_questions, selection.chapters, selectedSubjects, isMultiSubject, isFullBoardPaper, isBankingBlueprintPaper, isBanking, blueprintTopics, getSubjectChapters]);

  const isFormValid = useMemo(() => {
    return validate() === '';
  }, [selection, weightage, selectedSubjects, isMultiSubject, chapters, availableQuestionTypes, getSubjectChapters, getSubjectWeightTotal, blueprintTopics]);

  // Weightage is frozen only while a generation request is in flight. Editing it
  // afterwards re-enables "Generate Test"; regenerating single questions keeps
  // using the payload the paper was generated with (lastPayload).
  const weightageLocked = isGenerating;

  const hasChangedSinceGeneration = useMemo(() => {
    if (!generatedTest || !lastGeneratedPayloadStr) return true;
    if (!isFormValid) return false;
    try {
      return JSON.stringify(buildPayload()) !== lastGeneratedPayloadStr;
    } catch (e) {
      return true;
    }
  }, [generatedTest, lastGeneratedPayloadStr, isFormValid, selection, weightage, topicScope, availableQuestionTypes, chapters, selectedSubjects, isMultiSubject]);

  const runGeneration = useCallback(async (payload) => {
    setIsGenerating(true);
    setError('');
    setGeneratedTest(null);
    setSelectedQuestionIds([]);
    setIsRegenerating(false);
    setIsBulkRegenerating(false);
    setRegeneratingIds([]);
    setRecentlyRegeneratedIds([]);
    setActiveSectionFilter('ALL');
    setTestKind('Mixed');
    setSubjectiveMarks('');
    setLastPayload(payload);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GENERATE_TIMEOUT_MS);
    try {
      console.log('Sending request:', JSON.stringify(payload, null, 2));
      const response = await fetch(`${API_BASE}/api/generate-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      if (!response.ok) {
        const raw = await response.text();
        let detail = raw;
        try {
          const parsed = JSON.parse(raw);
          detail = formatDetail(parsed.detail ?? parsed.message ?? parsed.error ?? parsed);
        } catch (parseErr) {
          // Not JSON
        }
        console.error('Error response:', response.status, raw);
        const trimmed = (detail || '').slice(0, 400);
        if (response.status === 400 || response.status === 422) {
          throw new Error(
            `The server rejected these inputs${trimmed ? `: ${trimmed}` : '.'}`
          );
        }
        if (response.status === 502 || response.status === 503 || response.status === 504) {
          throw new Error(
            'The generation service is unavailable right now. It may still be starting up.'
          );
        }
        if (response.status >= 500) {
          throw new Error(
            `The server failed while generating${trimmed ? `: ${trimmed}` : '.'}`
          );
        }
        throw new Error(`Request failed (${response.status})${trimmed ? `: ${trimmed}` : ''}`);
      }
      const data = await response.json();
      console.log('Generated test:', data);
      setGeneratedTest(data);
      setLastGeneratedPayloadStr(JSON.stringify(payload));
      const qualityWarning = summarizeQualityWarning(data._metadata?.quality_warning);
      if (qualityWarning) {
        Notification.info('Info', qualityWarning);
      }
    } catch (err) {
      console.error('API Error:', err);
      let errorMsg = err.message;
      if (err.name === 'AbortError') {
        errorMsg = `Timed out after ${GENERATE_TIMEOUT_MS / 1000}s. Try fewer chapters or questions.`;
      } else if (err.message && err.message.includes('Failed to fetch')) {
        errorMsg = 'Network error. Please check your connection.';
      } else {
        errorMsg = shortenError(err.message);
      }
      setError(errorMsg);
      Notification.info('Info', errorMsg);
    } finally {
      clearTimeout(timeoutId);
      setIsGenerating(false);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      Notification.info('Info', validationError);
      return;
    }
    runGeneration(buildPayload());
  };

  const handleRegenerate = async (idsToRegenerate, isBulk = false) => {
    if (!idsToRegenerate || idsToRegenerate.length === 0) {
      Notification.warning(
        'Selection required',
        'Please select at least one question to regenerate.'
      );
      return;
    }
    if (!generatedTest) return;

    const validIds = Array.from(new Set(idsToRegenerate.map((id) => Number(id))));
    setIsRegenerating(true);
    setIsBulkRegenerating(isBulk);
    setRegeneratingIds(validIds);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GENERATE_TIMEOUT_MS);

    try {
      const requestBody = lastPayload || buildPayload();
      const response = await fetch(`${API_BASE}/api/regenerate-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request: requestBody,
          paper: generatedTest,
          regenerate_ids: validIds
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        const raw = await response.text();
        let detail = raw;
        try {
          const parsed = JSON.parse(raw);
          detail = formatDetail(parsed.detail ?? parsed.message ?? parsed.error ?? parsed);
        } catch (parseErr) {
          // Keep raw text
        }
        const trimmed = (detail || '').slice(0, 400);
        throw new Error(trimmed || `Regeneration failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('Regenerated test response:', data);

      setGeneratedTest(data);
      const replaced = Array.isArray(data.regenerated_ids) ? data.regenerated_ids : validIds;
      setRecentlyRegeneratedIds(replaced);

      setSelectedQuestionIds((prev) =>
        prev.filter((id) => !replaced.includes(Number(id)))
      );

      const notRegenerated = data._metadata?.regeneration?.not_regenerated;
      if (
        notRegenerated &&
        typeof notRegenerated === 'object' &&
        Object.keys(notRegenerated).length > 0
      ) {
        const skippedMsgs = Object.entries(notRegenerated).map(([qId, reason]) => {
          const qIndex = (data.questions || []).findIndex(
            (q) => Number(q.id) === Number(qId)
          );
          const qLabel = qIndex >= 0 ? `Q${qIndex + 1}` : `Question #${qId}`;
          return `${qLabel}: ${reason}`;
        });

        Notification.warning(
          'Some questions kept',
          `${skippedMsgs.join('; ')}. The original questions have been kept.`
        );
      } else {
        Notification.success(
          'Success',
          `Successfully regenerated ${replaced.length} question${replaced.length > 1 ? 's' : ''}!`
        );
      }
    } catch (err) {
      console.error('Regeneration error:', err);
      if (err.name === 'AbortError') {
        Notification.error(
          'Timed Out',
          `Regeneration timed out after ${GENERATE_TIMEOUT_MS / 1000}s. Please try again.`
        );
      } else {
        Notification.error(
          'Regeneration Failed',
          err.message || 'Could not regenerate questions. Please try again.'
        );
      }
    } finally {
      clearTimeout(timeoutId);
      setIsRegenerating(false);
      setIsBulkRegenerating(false);
      setRegeneratingIds([]);
    }
  };

  const handleToggleQuestionSelect = (questionId) => {
    const numId = Number(questionId);
    setSelectedQuestionIds((prev) =>
      prev.includes(numId)
        ? prev.filter((id) => id !== numId)
        : [...prev, numId]
    );
  };

  const questions = Array.isArray(generatedTest && generatedTest.questions)
    ? generatedTest.questions
    : [];
  const sections = Array.isArray(generatedTest && generatedTest.sections)
    ? generatedTest.sections
    : [];

  // A Banking paper: five-option questions, some grouped into sets on one passage, table or
  // puzzle, and sections named after the subject (with their own time limit).
  const bankingPaper = isBankingPaperResult(generatedTest);
  const questionSetById = useMemo(() => {
    const map = new Map();
    (Array.isArray(generatedTest?.question_sets) ? generatedTest.question_sets : [])
      .forEach((set) => map.set(set.set_id, set));
    return map;
  }, [generatedTest]);
  const paperSectionLabel = (sec) => (bankingPaper ? sec.title || sec.section : `Section ${sec.section}`);

  // Board papers lay out by CBSE section (A, B, C...) instead of by subject.
  // Letters come from the response: short sections can be missing.
  const paperSections = useMemo(() => (
    Array.isArray(generatedTest && generatedTest.paper_sections)
      ? generatedTest.paper_sections.filter(
        (sec) => sec && Array.isArray(sec.question_ids) && sec.question_ids.length > 0
      )
      : []
  ), [generatedTest]);
  const usePaperSections = paperSections.length > 0;
  const paperSectionByQuestionId = useMemo(() => {
    const map = new Map();
    paperSections.forEach((sec) => {
      sec.question_ids.forEach((qid) => map.set(Number(qid), sec));
    });
    return map;
  }, [paperSections]);

  // Only the CBSE Board pattern returns written questions next to objective ones.
  // A Board set holding both kinds asks which kind(s) become the test; one kind
  // decides the type on its own. Only Objective tests may span several subjects.
  const kindCounts = useMemo(() => {
    const list = Array.isArray(generatedTest?.questions) ? generatedTest.questions : [];
    const subjective = list.filter(isSubjectiveQuestion).length;
    return { objective: list.length - subjective, subjective };
  }, [generatedTest]);
  const isMixedBoardSet = isBoardPattern && kindCounts.objective > 0 && kindCounts.subjective > 0;
  const onlyObjectiveAllowed = isMultiSubject;
  const effectiveTestKind = isMixedBoardSet
    ? (onlyObjectiveAllowed ? 'Objective' : testKind)
    : (kindCounts.objective === 0 && kindCounts.subjective > 0 ? 'Subjective' : 'Objective');
  // The review list, tabs and PDF show only what the chosen test type keeps.
  const keptQuestions = useMemo(() => {
    if (effectiveTestKind === 'Objective') return questions.filter((q) => !isSubjectiveQuestion(q));
    if (effectiveTestKind === 'Subjective') return questions.filter(isSubjectiveQuestion);
    return questions;
  }, [questions, effectiveTestKind]);
  const keptIdSet = useMemo(() => new Set(keptQuestions.map((q) => Number(q.id))), [keptQuestions]);
  const keptCountOf = (ids) => (Array.isArray(ids) ? ids.filter((id) => keptIdSet.has(Number(id))).length : 0);

  // Drop ticks on questions the chosen type hides, so "Regenerate Selected" and the PDF match the list.
  useEffect(() => {
    setSelectedQuestionIds((prev) => {
      const next = prev.filter((id) => keptIdSet.has(Number(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [keptIdSet]);
  // Card numbers run 1..n over the kept questions, without gaps where the others were left out.
  const keptNumberById = useMemo(
    () => new Map(keptQuestions.map((q, i) => [Number(q.id), i + 1])),
    [keptQuestions]
  );

  const displayedQuestions = useMemo(() => {
    if (activeSectionFilter === 'ALL' || !activeSectionFilter) {
      return keptQuestions;
    }
    if (String(activeSectionFilter).startsWith('PS:')) {
      const letter = activeSectionFilter.slice(3);
      return keptQuestions.filter((q) => paperSectionByQuestionId.get(Number(q.id))?.section === letter);
    }
    return keptQuestions.filter((q) => q.subject === activeSectionFilter);
  }, [keptQuestions, activeSectionFilter, paperSectionByQuestionId]);

  // A section tab with nothing left for the chosen type disappears; fall back to All.
  useEffect(() => {
    if (activeSectionFilter && activeSectionFilter !== 'ALL' && keptQuestions.length > 0 && displayedQuestions.length === 0) {
      setActiveSectionFilter('ALL');
    }
  }, [activeSectionFilter, keptQuestions.length, displayedQuestions.length]);

  const isAllDisplayedSelected =
    displayedQuestions.length > 0 &&
    displayedQuestions.every((q) => selectedQuestionIds.includes(Number(q.id)));
  const hasSomeDisplayedSelected =
    displayedQuestions.length > 0 &&
    displayedQuestions.some((q) => selectedQuestionIds.includes(Number(q.id)));

  const handleToggleSelectAll = (e) => {
    if (e.target.checked) {
      const displayedIds = displayedQuestions.map((q) => Number(q.id));
      setSelectedQuestionIds((prev) => Array.from(new Set([...prev, ...displayedIds])));
    } else {
      const displayedIds = new Set(displayedQuestions.map((q) => Number(q.id)));
      setSelectedQuestionIds((prev) => prev.filter((id) => !displayedIds.has(id)));
    }
  };

  const needsSubjectiveMarks = keptQuestions.some(
    (q) => isSubjectiveQuestion(q) && getSubjectiveMarks(q) === null
  );
  const subjectiveMarksValue = Number(subjectiveMarks);
  const subjectiveMarksValid = Number.isFinite(subjectiveMarksValue) && subjectiveMarksValue > 0;

  const handleCreateTest = () => {
    if (needsSubjectiveMarks && !subjectiveMarksValid) {
      Notification.error('Marks needed', 'Enter the marks for each subjective question before creating the test.');
      return;
    }
    const payload = {
      ...generatedTest,
      questions: keptQuestions.map((question) => {
        if (isSubjectiveQuestion(question)) {
          // Written answers are graded by the teacher: their own marks, never negative.
          return {
            ...question,
            marks: getSubjectiveMarks(question) ?? subjectiveMarksValue,
            negative_marks: 0
          };
        }
        const isInteger = isIntegerQuestion(question, selection.question_types);
        const resolvedDiagram =
          question.diagram && question.diagram.required && question.diagram.rendered_url
            ? {
              ...question.diagram,
              absolute_url: diagramUrl(question.diagram.rendered_url)
            }
            : question.diagram;

        const correctAnswersList = Array.isArray(question.correct_answers) && question.correct_answers.length > 0
          ? question.correct_answers
          : (question.correct_answer ? [question.correct_answer] : []);

        const qMarks = getQuestionMarks(question, generatedTest, selection.question_types);
        const qNegMarks = getQuestionNegativeMarks(question, generatedTest, selection.question_types);

        const mappingPairs = getCorrectMappingPairs(question);
        const mappingStr = mappingPairs ? mappingPairs.map((p) => `${p.left} → ${p.right}`).join(', ') : '';

        const base = {
          ...question,
          ...(resolvedDiagram ? { diagram: resolvedDiagram } : {}),
          ...(qMarks !== null && qMarks !== undefined ? { marks: qMarks } : {}),
          ...(qNegMarks !== null && qNegMarks !== undefined ? { negative_marks: Math.abs(Number(qNegMarks)) } : {}),
          correct_answer: question.correct_answer || correctAnswersList.join(',') || mappingStr,
          correct_answers: correctAnswersList.length > 0 ? correctAnswersList : (mappingStr ? [mappingStr] : []),
          ...(mappingPairs ? { correct_mapping: question.correct_mapping || question.correctMapping } : {})
        };

        if (isInteger) {
          return {
            ...base,
            type: 'integer_type',
            question_type: 'integer_type',
            options: []
          };
        }
        return base;
      })
    };

    // Convert chapter unique IDs back to chapter names for the handoff
    const handoffSelection = {
      ...selection,
      chapters: selection.chapters.map((id) => {
        const found = chapters.find((c) => c.id === id || c.value === id);
        return found ? found.name : id;
      }),
      testType: effectiveTestKind,
      // Every chapter in the picker ticked: the test is a Full syllabus test.
      fullSyllabus: chapters.length > 0 && chapters.every(
        (c) => selection.chapters.includes(c.id) || selection.chapters.includes(c.value)
      )
    };

    if (bankingPaper) {
      // Each set goes over as one comprehension question.
      const collapsed = collapseBankingSets(payload.questions, generatedTest.question_sets);
      payload.questions = collapsed.questions;
      payload.sections = collapsed.sections;
      // The paper decides the sections and topics (a full mock has none chosen on the form).
      const unique = (list) => Array.from(new Set(list.filter(Boolean)));
      handoffSelection.subject = unique(questions.map((q) => q.subject));
      handoffSelection.chapters = unique(questions.map((q) => q.chapter));
      handoffSelection.question_types = [];
      // What the paper was generated as, not what the form may have been changed to since.
      if (lastPayload?.paper_mode === 'full') handoffSelection.syllabus = 'Full';
    }

    startAiTestHandoff(handoffSelection, payload, (subjectResolved) => {
      if (!subjectResolved) {
        const subName = Array.isArray(selection.subject)
          ? selection.subject.join(', ')
          : selection.subject;
        Notification.error(
          'Subject not matched',
          `No subject named "${subName}" exists for this institute. Pick one on the next screen.`
        );
      }
      saveGenerationSnapshot({
        selection, weightage, topicScope, chapters, classes, subjects,
        generatedTest, lastPayload, lastGeneratedPayloadStr,
        testKind, subjectiveMarks,
      });
      history.push({
        pathname: '/create-test',
        state: { key: location?.state?.key, from: 'aulasAi' }
      });
    });
  };

  const renderSelect = (
    label,
    field,
    value,
    options,
    onChange,
    disabledHint,
    mode,
    isDisabled,
    notFoundContent,
    headerActions
  ) => {
    const isMultiple = mode === 'multiple';
    const computedValue = isMultiple
      ? (Array.isArray(value) ? value : (value ? [value] : []))
      : (value || undefined);
    const disabled = isDisabled !== undefined ? isDisabled : (loading[field] || options.length === 0);

    return (
      <div className="form-group" style={{ flex: 1 }}>
        <div className="section-label-row">
          <label>{label}<span className="mandatory-star">*</span></label>
          {headerActions}
        </div>
        <div className="dropdown-relative-container">
          <Select
            getPopupContainer={(triggerNode) =>
              triggerNode
                ? triggerNode.closest('.dropdown-relative-container') ||
                triggerNode.parentNode ||
                triggerNode.parentElement
                : document.body
            }
            mode={mode}
            value={computedValue}
            onChange={(val) => onChange({ target: { value: val, name: field } })}
            disabled={disabled}
            notFoundContent={notFoundContent}
            placeholder={
              loading[field]
                ? `Loading ${field}...`
                : disabled
                  ? disabledHint
                  : isMultiple
                    ? `Select ${label.toLowerCase()}(s)`
                    : `Select ${label.toLowerCase()}`
            }
            style={{ width: '100%' }}
            size="large"
            maxTagCount="responsive"
            suffixIcon={<img src={dropdownArrow} alt="dropdown" />}
          >
            {options.map((option) => (
              <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
    );
  };

  const getCorrectAnswers = (question) => {
    if (Array.isArray(question?.correct_answers) && question.correct_answers.length > 0) {
      return question.correct_answers.map((ans) => String(ans).trim().toUpperCase()).filter(Boolean);
    }
    const single = String(question?.correct_answer ?? question?.answer ?? '').trim().toUpperCase();
    if (single) {
      if (single.includes(',')) {
        return single.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
      }
      return [single];
    }
    return [];
  };

  const renderOptions = (options, questionOrAnswer) => {
    if (!options) return null;
    const entries = Array.isArray(options)
      ? options.map((value, i) => [OPTION_LETTERS[i] || String(i + 1), value])
      : Object.entries(options);
    const correctList = typeof questionOrAnswer === 'object' && questionOrAnswer !== null
      ? getCorrectAnswers(questionOrAnswer)
      : (questionOrAnswer ? [String(questionOrAnswer).trim().toUpperCase()] : []);
    return entries.map(([letter, value]) => {
      const isCorrect = correctList.includes(letter.toUpperCase());
      return (
        <div key={letter} className={`option-item ${isCorrect ? 'correct' : ''}`}>
          <div className={`option-letter ${isCorrect ? 'correct' : ''}`}>{letter}</div>
          <div className="option-text">
            <MathText>
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </MathText>
          </div>
        </div>
      );
    });
  };

  const answerText = (question) => {
    const mappingPairs = getCorrectMappingPairs(question);
    if (mappingPairs && mappingPairs.length > 0) {
      return {
        isMatrixMapping: true,
        pairs: mappingPairs,
        summary: mappingPairs.map((p) => `${p.left} → ${p.right}`).join(', ')
      };
    }

    const correctList = getCorrectAnswers(question);
    if (correctList.length === 0) return null;

    if (correctList.length === 1) {
      const letter = correctList[0];
      if (!Array.isArray(question.options)) return letter;
      const index = OPTION_LETTERS.indexOf(letter);
      const value = index >= 0 ? question.options[index] : undefined;
      return value === undefined ? letter : { letter, value: String(value) };
    }

    const joinedLetters = correctList.join(', ');
    if (!Array.isArray(question.options)) {
      return { letter: joinedLetters, letters: correctList, isMultiple: true, items: [] };
    }

    const items = correctList.map((letter) => {
      const index = OPTION_LETTERS.indexOf(letter);
      const val = index >= 0 ? question.options[index] : undefined;
      return { letter, value: val !== undefined ? String(val) : '' };
    });

    return {
      letter: joinedLetters,
      letters: correctList,
      isMultiple: true,
      items: items.filter((item) => item.value)
    };
  };

  // Plain description of one question for the PDF export (paperPdf.js), built
  // with the same helpers the cards use so the PDF matches what is on screen.
  // `preface` is printed above the question: a Banking set's passage, before its first question.
  const buildExportQuestion = (question, number, preface = '') => {
    const qType = String(question.question_type || question.type || '').toLowerCase();
    const isWritten = WRITTEN_ANSWER_TYPES.includes(qType);
    const isCase = qType === 'case_based';
    const isInteger = !isWritten && !isCase && isIntegerQuestion(question, selection.question_types);
    const correctLetters = getCorrectAnswers(question).filter((l) => OPTION_LETTERS.includes(l));
    const rawOptions = !isInteger && Array.isArray(question.options) ? question.options : [];
    const options = rawOptions.map((value, i) => ({
      letter: OPTION_LETTERS[i] || String(i + 1),
      text: typeof value === 'object' ? JSON.stringify(value) : String(value),
      correct: correctLetters.includes(OPTION_LETTERS[i])
    }));

    const matrixCols = getMatrixColumns(question);
    const matrix = matrixCols.hasColumns
      ? {
        colA: matrixCols.colA.map((item, idx) => parseMatrixItem(item, DEFAULT_COL_A_LETTERS[idx] || OPTION_LETTERS[idx])),
        colB: matrixCols.colB.map((item, idx) => parseMatrixItem(item, DEFAULT_COL_B_LETTERS[idx]))
      }
      : null;

    const subQuestions = isCase && Array.isArray(question.sub_questions)
      ? question.sub_questions.map((sub, idx) => ({
        numeral: ROMAN_NUMERALS[idx] || String(idx + 1),
        text: sub.question || '',
        marks: sub.marks,
        answer: sub.answer || ''
      }))
      : [];

    let answer;
    if (isWritten) {
      answer = {
        kind: 'written',
        // A two-choice descriptive task: every choice's model answer, and its points marked with the choice.
        modelAnswer: descriptiveChoices(question).length > 0
          ? descriptiveChoices(question).map((c) => `(${c.label}) ${c.modelAnswer}`).join('\n\n')
          : question.model_answer || question.correct_answer || '',
        markingPoints: descriptiveChoices(question).length > 0
          ? descriptiveChoices(question).flatMap((c) => c.markingPoints.map((mp) => ({ ...mp, point: `(${c.label}) ${mp.point}` })))
          : normalizeMarkingPoints(question.marking_points)
      };
    } else if (isCase) {
      answer = { kind: 'case', text: question.correct_answer || '' };
    } else if (isInteger) {
      answer = { kind: 'text', text: String(question.correct_answer ?? question.answer ?? 'N/A') };
    } else {
      const a = answerText(question);
      let text = 'N/A';
      if (a && typeof a === 'object' && a.isMatrixMapping) {
        // New-shape matrix match: one option letter plus the mapping it stands for.
        text = options.length > 0 && correctLetters.length === 1
          ? `(${correctLetters[0]}) ${a.summary}`
          : a.summary;
      } else if (a && typeof a === 'object' && a.isMultiple) {
        text = a.letters.map((l) => `(${l})`).join(', ');
      } else if (a && typeof a === 'object') {
        text = `(${a.letter}) ${a.value}`;
      } else if (a) {
        text = String(a);
      }
      answer = { kind: 'text', text };
    }

    const typeLabel = (BOARD_QUESTION_TYPES.find((t) => t.value === qType)
      || QUESTION_TYPES.find((t) => t.value === resolveQuestionType(question, selection.question_types)))?.label;

    return {
      number,
      text: [preface, descriptivePromptText(question) || question.statement || ''].filter(Boolean).join('\n\n'),
      subject: isMultiSubject ? question.subject : '',
      chapter: question.chapter || '',
      typeLabel: typeLabel || '',
      marks: getQuestionMarks(question, generatedTest, selection.question_types),
      negativeMarks: getQuestionNegativeMarks(question, generatedTest, selection.question_types),
      diagramUrl: question.diagram && question.diagram.required
        ? diagramUrl(question.diagram.rendered_url)
        : null,
      options,
      matrix,
      subQuestions,
      answer,
      explanation: question.explanation || question.solution || ''
    };
  };

  // Ticked questions only, when any are ticked; otherwise the whole paper.
  // Grouped by CBSE section for Board papers, by subject for multi-subject ones.
  const buildExportModel = () => {
    const selectedSet = new Set(selectedQuestionIds.map(Number));
    const exportQuestions = selectedSet.size > 0
      ? keptQuestions.filter((q) => selectedSet.has(Number(q.id)))
      : keptQuestions;

    const groups = [];
    let current = null;
    exportQuestions.forEach((question, index) => {
      let key = '';
      let heading = '';
      if (usePaperSections) {
        const sec = paperSectionByQuestionId.get(Number(question.id));
        key = sec ? `PS:${sec.section}` : '';
        heading = sec ? (bankingPaper ? paperSectionLabel(sec) : `Section ${sec.section} — ${sec.title}`) : '';
      } else if (sections.length > 1) {
        const secIndex = sections.findIndex((s) => s.subject === question.subject);
        key = question.subject || '';
        heading = secIndex >= 0 ? `Section ${SECTION_LETTERS[secIndex] || secIndex + 1}: ${question.subject}` : '';
      }
      if (!current || current.key !== key) {
        current = { key, heading, questions: [] };
        groups.push(current);
      }
      // A set's passage goes above the first of its questions in the export.
      const set = question.set_id ? questionSetById.get(question.set_id) : null;
      const prev = exportQuestions[index - 1];
      const preface = set && (!prev || prev.set_id !== question.set_id) ? setPassageText(set) : '';
      current.questions.push(buildExportQuestion(question, index + 1, preface));
    });

    const sumMarks = (list) => list.reduce((sum, q) => sum + (Number(q.marks) || 0), 0);
    groups.forEach((g) => {
      const marks = sumMarks(g.questions);
      g.subheading = g.heading
        ? `${g.questions.length} question${g.questions.length === 1 ? '' : 's'}${marks > 0 ? ` · ${formatMarks(marks)} marks` : ''}`
        : '';
    });

    const allQuestions = groups.flatMap((g) => g.questions);
    const isWholePaper = exportQuestions.length === questions.length;
    const totalMarks = isWholePaper && generatedTest?.total_marks !== undefined && generatedTest?.total_marks !== null
      ? generatedTest.total_marks
      : (sumMarks(allQuestions) || null);
    const subjectLabel = Array.isArray(generatedTest?.subject)
      ? generatedTest.subject.join(', ')
      : (generatedTest?.subject || selectedSubjects.join(', '));

    return {
      title: (selection.title && selection.title.trim()) || `${subjectLabel || 'Generated'} Test`,
      exam: generatedTest?.exam || '',
      subject: subjectLabel,
      totalMarks,
      duration: selection.duration_mins || '',
      groups
    };
  };

  const handleDownloadPdf = async (withSolutions) => {
    if (downloadingPdf) return;
    const kind = withSolutions ? 'solutions' : 'paper';
    setDownloadingPdf(kind);
    try {
      // Let the loader paint before the export takes over the main thread.
      await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
      await downloadPaperPdf(buildExportModel(), { withSolutions });
    } catch (err) {
      console.error('PDF download failed:', err);
      Notification.error('Download failed', 'Could not create the PDF. Please try again.');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const metadata = (generatedTest && generatedTest._metadata) || {};
  const shortfall =
    typeof metadata.n_generated === 'number' &&
    typeof metadata.n_requested === 'number' &&
    metadata.n_generated < metadata.n_requested;

  return (
    <ConfigProvider
      getPopupContainer={(triggerNode) =>
        triggerNode
          ? triggerNode.closest('.dropdown-relative-container') ||
          triggerNode.parentNode ||
          triggerNode.parentElement
          : document.body
      }
    >
      <div className="test-gen-tab" style={{ paddingTop: '64px' }} ref={pageRef}>
        <PageHeader
          title="Generate with aulasAi"
          onBackPress={() => history.push('/test')}
        />
        <form onSubmit={handleSubmit} className="test-gen-form">
          <div className="form-row">
            {renderSelect(
              'Pattern',
              'patterns',
              selection.pattern,
              patterns,
              handlePatternChange,
              'No patterns available'
            )}
            {renderSelect(
              terms.class,
              'classes',
              selection.class,
              classes,
              handleClassChange,
              'Select a pattern first'
            )}
            {/* A full Banking mock covers every section, so there is none to pick */}
            {!isBankingFull && renderSelect(
              terms.subject,
              'subjects',
              selection.subject,
              isBanking ? withPendingSections(subjects, blueprint) : subjects,
              handleSubjectChange,
              !selection.pattern ? 'Select a pattern first' : `Select a ${terms.class.toLowerCase()} first`,
              isBoardPattern || isBankingSectional ? undefined : 'multiple',
              loading.subjects || subjects.length === 0 || !selection.class,
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="ant-select-empty"
                description={`No ${terms.subject.toLowerCase()} found !`}
              />
            )}
          </div>

          {/* Banking: the whole mock, one whole section, or a custom paper */}
          {isBanking && (
            <div className="form-group">
              <label>Paper Mode<span className="mandatory-star">*</span></label>
              <Radio.Group
                value={selection.paper_mode}
                onChange={(e) => handlePaperModeChange(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="large"
                className="paper-mode-group"
              >
                {BANKING_PAPER_MODES.map((mode) => (
                  <Radio.Button key={mode.value} value={mode.value}>{mode.label}</Radio.Button>
                ))}
              </Radio.Group>
            </div>
          )}

          {/* Board only: Full CBSE paper or Custom paper */}
          {isBoardPattern && (
            <div className="form-group">
              <label>Paper Mode<span className="mandatory-star">*</span></label>
              <Radio.Group
                value={selection.paper_mode === 'full' ? 'full' : 'custom'}
                onChange={(e) => handlePaperModeChange(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="large"
                className="paper-mode-group"
              >
                <Radio.Button value="full">Full CBSE paper</Radio.Button>
                <Radio.Button value="custom">Custom paper</Radio.Button>
              </Radio.Group>
              {isFullBoardPaper && (
                <p className="input-hint">
                  {fullBoardBlueprint.questions} questions · {fullBoardBlueprint.marks} marks.
                  Question types and their counts follow the CBSE blueprint and are spread
                  across the selected chapters by weightage.
                </p>
              )}
            </div>
          )}

          {/* Chapters Picker (a full Banking mock or section follows its blueprint instead) */}
          {!isBankingBlueprintPaper && (
            <div className="form-group">
              <div className="section-label-row">
                <label>{terms.chapters}<span className="mandatory-star">*</span></label>
                {chapters.length > 0 && (
                  <div className="section-actions">
                    <button
                      type="button"
                      className="link-btn"
                      onClick={handleSelectAllAllChapters}
                    >
                      Select all
                    </button>
                    {selection.chapters.length > 0 && (
                      <button
                        type="button"
                        className="link-btn"
                        onClick={handleClearAllChapters}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
              {loading.chapters ? (
                <div className="chapters-empty">
                  <span className="spinner dark"></span> Loading {terms.chapters.toLowerCase()}...
                </div>
              ) : chapters.length === 0 ? (
                <div className="chapters-empty disabled">
                  <StopOutlined className="chapters-disabled-icon" />
                  <span>
                    {selection.pattern && selection.class && selectedSubjects.length > 0
                      ? `No ${terms.chapters.toLowerCase()} available for the selected ${terms.subject.toLowerCase()}(s)`
                      : `Select a pattern, ${terms.class.toLowerCase()} and ${terms.subject.toLowerCase()} to load ${terms.chapters.toLowerCase()}`}
                  </span>
                </div>
              ) : (isMultiSubject || showChapterClass) ? (
                <div className="chapters-multi-container">
                  {chaptersBySubject.map((group) => {
                    const groupChapterIds = group.chapters.map((c) => c.id);
                    const selectedCount = group.chapters.filter((c) =>
                      selection.chapters.includes(c.id)
                    ).length;
                    const classGroups = showChapterClass ? groupChaptersByClass(group.chapters) : [];
                    const hasMultipleClasses = classGroups.length > 1;

                    return (
                      <div key={group.subjectValue} className="subject-chapter-group">
                        <div className="subject-group-header">
                          <div className="subject-group-title-row">
                            <h4 className="subject-group-title">{group.subjectName}</h4>
                            <span className="subject-group-badge">
                              {selectedCount > 0
                                ? `${selectedCount} of ${group.chapters.length} selected`
                                : `${group.chapters.length} chapters`}
                            </span>
                          </div>
                          <div className="subject-group-actions">
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => handleSelectAllSubjectChapters(group.subjectValue)}
                            >
                              Select all
                            </button>
                            {selectedCount > 0 && (
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => handleClearSubjectChapters(group.subjectValue)}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                        {hasMultipleClasses ? (
                          <div className="subject-class-groups">
                            {classGroups.map((classGroup) => {
                              const classChapterIds = classGroup.chapters.map((c) => c.id);
                              const classSelectedCount = classGroup.chapters.filter((c) =>
                                selection.chapters.includes(c.id)
                              ).length;

                              return (
                                <div key={classGroup.classKey} className="class-chapter-group">
                                  <div className="class-group-header">
                                    <div className="class-group-title-row">
                                      <span className="class-group-title">{classGroup.className}</span>
                                      <span className="class-group-badge">
                                        {classSelectedCount > 0
                                          ? `${classSelectedCount} of ${classGroup.chapters.length} selected`
                                          : `${classGroup.chapters.length} chapters`}
                                      </span>
                                    </div>
                                    <div className="class-group-actions">
                                      <button
                                        type="button"
                                        className="link-btn class-action-btn"
                                        onClick={() => {
                                          const newSel = Array.from(
                                            new Set([...selection.chapters, ...classChapterIds])
                                          );
                                          setSelection((prev) => ({ ...prev, chapters: newSel }));
                                          const newWeights = distributeSubjectEvenly(group.subjectValue, newSel);
                                          setWeightage((prev) => ({ ...prev, ...newWeights }));
                                        }}
                                      >
                                        Select all
                                      </button>
                                      {classSelectedCount > 0 && (
                                        <button
                                          type="button"
                                          className="link-btn class-action-btn"
                                          onClick={() => {
                                            const newSel = selection.chapters.filter(
                                              (id) => !classChapterIds.includes(id)
                                            );
                                            setSelection((prev) => ({ ...prev, chapters: newSel }));
                                            const newWeights = distributeSubjectEvenly(group.subjectValue, newSel);
                                            setWeightage((prev) => ({ ...prev, ...newWeights }));
                                          }}
                                        >
                                          Clear
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="subject-chapters-grid">
                                    {classGroup.chapters.map((chapter) => {
                                      const checked = selection.chapters.includes(chapter.id);
                                      return (
                                        <Checkbox
                                          key={chapter.id}
                                          className={`chapter-chip ${checked ? 'selected' : ''}`}
                                          checked={checked}
                                          onChange={() => toggleChapter(chapter.id)}
                                        >
                                          <span>{chapter.label}</span>
                                          {isBanking && isSetTopic(chapter) && <span className="set-topic-tag">Set</span>}
                                        </Checkbox>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="subject-chapters-grid">
                            {group.chapters.map((chapter) => {
                              const checked = selection.chapters.includes(chapter.id);
                              return (
                                <Checkbox
                                  key={chapter.id}
                                  className={`chapter-chip ${checked ? 'selected' : ''}`}
                                  checked={checked}
                                  onChange={() => toggleChapter(chapter.id)}
                                >
                                  <span>{chapter.label}</span>
                                  {isBanking && isSetTopic(chapter) && <span className="set-topic-tag">Set</span>}
                                </Checkbox>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="chapters-grid">
                  {chapters.map((chapter) => {
                    const checked = selection.chapters.includes(chapter.id);
                    return (
                      <Checkbox
                        key={chapter.id}
                        className={`chapter-chip ${checked ? 'selected' : ''}`}
                        checked={checked}
                        onChange={() => toggleChapter(chapter.id)}
                      >
                        <span>{chapter.label}</span>
                        {isBanking && isSetTopic(chapter) && <span className="set-topic-tag">Set</span>}
                      </Checkbox>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Per-Subject Weightage */}
          {selection.chapters.length > 0 && !isBankingBlueprintPaper && (
            <div className="form-group">
              <label>{isBanking ? 'Topic' : 'Chapter'} Weightage<span className="mandatory-star">*</span></label>
              {isMultiSubject ? (
                <div className="multi-subject-weightage-container">
                  {selectedSubjects.map((subj) => {
                    const subjChs = getSubjectChapters(subj);
                    if (subjChs.length === 0) return null;
                    const total = getSubjectWeightTotal(subj);
                    const balanced = isSubjectWeightBalanced(subj);

                    return (
                      <div key={subj} className="subject-weightage-group">
                        <div className="section-label-row">
                          <h4 className="subject-weightage-title">{subj} Weightage</h4>
                          <div className="section-actions">
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => handleDistributeSubjectEvenly(subj)}
                              disabled={weightageLocked}
                            >
                              Distribute evenly
                            </button>
                          </div>
                        </div>
                        <div className="weightage-panel">
                          {subjChs.map((chapter) => (
                            <React.Fragment key={chapter.id}>
                              <div className="weightage-row">
                                <span className="weightage-name">{chapter.name}</span>
                                {renderTopicScopeToggle(chapter)}
                                <div className="weightage-input">
                                  <Input
                                    type="number"
                                    min={0.01}
                                    max={100}
                                    step={0.01}
                                    value={weightage[chapter.id] ?? ''}
                                    onChange={(e) => handleWeightChange(chapter.id, e.target.value)}
                                    onBlur={() => handleWeightBlur(chapter.id)}
                                    onKeyDown={handleWeightKeyDown}
                                    onPaste={blockInvalidNumberPaste}
                                    disabled={weightageLocked}
                                    suffix="%"
                                    style={{ width: 100 }}
                                  />
                                </div>
                              </div>
                              {renderTopicScopePanel(chapter)}
                            </React.Fragment>
                          ))}
                          <div className={`weightage-total ${balanced ? 'valid' : 'invalid'}`}>
                            <span>{subj} Total</span>
                            <span>
                              {total.toFixed(2)}%
                              {balanced ? ' ✓' : ` — ${getWeightageMessage(subj)}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="weightage-panel">
                  <div className="section-label-row" style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      Weights must sum to 100%
                    </span>
                    <div className="section-actions">
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => handleDistributeSubjectEvenly(selectedSubjects[0])}
                        disabled={weightageLocked}
                      >
                        Distribute evenly
                      </button>
                    </div>
                  </div>
                  {chapters
                    .filter((c) => selection.chapters.includes(c.id))
                    .map((chapter) => (
                      <React.Fragment key={chapter.id}>
                        <div className="weightage-row">
                          <span className="weightage-name">{chapter.name}</span>
                          {renderTopicScopeToggle(chapter)}
                          <div className="weightage-input">
                            <Input
                              type="number"
                              min={0.01}
                              max={100}
                              step={0.01}
                              value={weightage[chapter.id] ?? ''}
                              onChange={(e) => handleWeightChange(chapter.id, e.target.value)}
                              onBlur={() => handleWeightBlur(chapter.id)}
                              onKeyDown={handleWeightKeyDown}
                              onPaste={blockInvalidNumberPaste}
                              disabled={weightageLocked}
                              suffix="%"
                              style={{ width: 100 }}
                            />
                          </div>
                        </div>
                        {renderTopicScopePanel(chapter)}
                      </React.Fragment>
                    ))}
                  {(() => {
                    const subj = selectedSubjects[0];
                    const total = subj ? getSubjectWeightTotal(subj) : 0;
                    const balanced = subj ? isSubjectWeightBalanced(subj) : false;
                    return (
                      <div className={`weightage-total ${balanced ? 'valid' : 'invalid'}`}>
                        <span>Total</span>
                        <span>
                          {total.toFixed(2)}%
                          {balanced ? ' ✓' : ` — ${getWeightageMessage(subj)}`}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Number of Questions & Difficulty */}
          <div className="form-row">
            {!isFullBoardPaper && !isBankingBlueprintPaper && (
              <div className="form-group">
                <label>Number of Questions<span className="mandatory-star">*</span></label>
                <Input
                  type="number"
                  name="n_questions"
                  value={selection.n_questions}
                  onChange={handleFieldChange}
                  onKeyDown={blockInvalidNumberKeys}
                  onPaste={blockInvalidNumberPaste}
                  placeholder={`max ${maxQuestions}`}
                  min={Math.max(1, selectedSubjects.length)}
                  max={maxQuestions}
                  required
                  size="large"
                  status={questionCountError ? 'error' : undefined}
                />
                {questionCountError && (
                  <p className="input-hint input-error">{questionCountError}</p>
                )}
              </div>
            )}
            <div className="form-group">
              <div className="section-label-row">
                <label>Difficulty<span className="mandatory-star">*</span></label>
                <Radio.Group
                  value={isDifficultyMix ? 'mix' : 'single'}
                  onChange={(e) => handleDifficultyModeChange(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                  className="difficulty-mode-group"
                >
                  <Radio.Button value="single">Single level</Radio.Button>
                  <Radio.Button value="mix">Mix</Radio.Button>
                </Radio.Group>
              </div>
              {isDifficultyMix ? (
                <div className="difficulty-mix-panel">
                  <div className="difficulty-mix-inputs">
                    {DIFFICULTY_MIX_LEVELS.map((level) => (
                      <div key={level} className="difficulty-mix-field">
                        <span className="difficulty-mix-label">{level}</span>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={selection.difficulty_mix?.[level] ?? ''}
                          onChange={(e) => handleDifficultyMixChange(level, e.target.value)}
                          onKeyDown={handleWeightKeyDown}
                          onPaste={blockInvalidNumberPaste}
                          placeholder="0"
                          suffix="%"
                          size="large"
                        />
                      </div>
                    ))}
                  </div>
                  <div className={`weightage-total ${isDifficultyMixBalanced ? 'valid' : 'invalid'}`}>
                    <span>Total</span>
                    <span>
                      {formatMarks(difficultyMixTotal)}%
                      {isDifficultyMixBalanced
                        ? ' ✓'
                        : ` — ${difficultyMixTotal < 100 ? 'Add' : 'Remove'} ${formatMarks(Math.abs(100 - difficultyMixTotal))}% so the levels add up to 100%`}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="dropdown-relative-container">
                  <Select
                    getPopupContainer={(triggerNode) =>
                      triggerNode
                        ? triggerNode.closest('.dropdown-relative-container') ||
                        triggerNode.parentNode ||
                        triggerNode.parentElement
                        : document.body
                    }
                    value={selection.difficulty || undefined}
                    onChange={(val) => handleFieldChange({ target: { name: 'difficulty', value: val } })}
                    placeholder="Select difficulty"
                    style={{ width: '100%' }}
                    size="large"
                    suffixIcon={<img src={dropdownArrow} alt="dropdown" />}
                  >
                    {DIFFICULTIES.map((level) => (
                      <Select.Option key={level} value={level}>
                        {level}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Question types (fixed by the blueprint for a full CBSE paper; Banking topics decide their own) */}
          {!isFullBoardPaper && !isBanking && (
            <div className="form-group">
              <label>Question Types<span className="mandatory-star">*</span></label>
              <div className="chapters-grid no-scroll">
                {availableQuestionTypes.map((type) => {
                  const checked = selection.question_types.includes(type.value);
                  return (
                    <Checkbox
                      key={type.value}
                      className={`chapter-chip ${checked ? 'selected' : ''}`}
                      checked={checked}
                      onChange={() => toggleQuestionType(type.value)}
                    >
                      <span>{type.label}</span>
                    </Checkbox>
                  );
                })}
              </div>
              {isBoardPattern && selection.question_types.length > 1 && (
                <p className="input-hint">
                  The question count is split evenly across the selected types.
                </p>
              )}
            </div>
          )}

          {/* Test Title (Optional) */}
          <div className="form-group">
            <label>Test Title (Optional)</label>
            <Input
              name="title"
              value={selection.title}
              onChange={handleFieldChange}
              placeholder="Enter test title"
              size="large"
            />
          </div>

          <Button
            type="primary"
            htmlType="submit"
            className="generate-btn"
            disabled={!isFormValid || !hasChangedSinceGeneration || isGenerating}
            loading={isGenerating}
            size="large"
          >
            {isGenerating ? 'Generating...' : 'Generate Test'}
          </Button>
        </form>

        <Modal
          visible={isGenerating}
          centered
          footer={null}
          closable={false}
          maskClosable={false}
          width={440}
          className="generating-modal"
        >
          <div className="generating-modal-content">
            <Spin size="large" />
            <p className="elapsed-counter">
              {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
            </p>
            <h3 className="generating-modal-title">Generating Questions...</h3>
            <p className="generating-modal-text">
              {isBankingFull
                ? `Generating a full ${blueprint?.total_questions || 100}-question mock — this can take a few minutes. Please keep this tab open.`
                : isFullBoardPaper
                  ? `Generating a full CBSE paper with written answers — this normally takes 1.5 to 2 minutes. Please keep this tab open.`
                  : isMultiSubject
                    ? `Generating across ${selectedSubjects.length} subjects in parallel — this normally takes about 50 seconds. Please keep this tab open.`
                    : `Generating questions — this normally takes 30-50 seconds. Please keep this tab open.`}
            </p>
            {elapsed > 240 && (
              <p className="elapsed-note">
                Still working. The request is given up to{' '}
                {GENERATE_TIMEOUT_MS / 1000} seconds before timing out.
              </p>
            )}
          </div>
        </Modal>

        <PageDataLoader visible={handoffLoading} />

        {generatedTest && (
          <div className="results-section">
            <div className="results-header" style={{ top: stickyTop }}>
              <h3>Generated Test</h3>
              {questions.length > 0 && (
                <div className="download-actions">
                  <Button
                    htmlType="button"
                    icon={<DownloadOutlined />}
                    className="radius-100 download-pdf-btn"
                    loading={downloadingPdf === 'paper'}
                    disabled={!!downloadingPdf || isRegenerating}
                    onClick={() => handleDownloadPdf(false)}
                  >
                    Without Solutions{selectedQuestionIds.length > 0 ? ` (${selectedQuestionIds.length})` : ''}
                  </Button>
                  <Button
                    htmlType="button"
                    icon={<DownloadOutlined />}
                    className="radius-100 download-pdf-btn"
                    loading={downloadingPdf === 'solutions'}
                    disabled={!!downloadingPdf || isRegenerating}
                    onClick={() => handleDownloadPdf(true)}
                  >
                    With Solutions{selectedQuestionIds.length > 0 ? ` (${selectedQuestionIds.length})` : ''}
                  </Button>
                  <Button
                    htmlType="button"
                    disabled={handoffLoading}
                    className="purpleBtnWrap radius-100 purpleButtonHoverEffect"
                    style={{ minWidth: 150 }}
                    onClick={handleCreateTest}
                  >
                    CREATE TEST
                  </Button>
                </div>
              )}
            </div>
            <div className="test-meta">
              {generatedTest.exam && <span>{generatedTest.exam}</span>}
              {generatedTest.subject && (
                <span>
                  {Array.isArray(generatedTest.subject)
                    ? generatedTest.subject.join(', ')
                    : generatedTest.subject}
                </span>
              )}
              {generatedTest.difficulty && <span>{generatedTest.difficulty}</span>}
              <span>{generatedTest.totalQuestions ?? questions.length} questions</span>
              {generatedTest.total_marks !== undefined && generatedTest.total_marks !== null && (
                <span>Maximum marks: {formatMarks(generatedTest.total_marks)}</span>
              )}
              {generatedTest.duration_minutes ? <span>{generatedTest.duration_minutes} minutes</span> : null}
              {metadata.difficulty_generated && typeof metadata.difficulty_generated === 'object' && (
                <span>
                  {DIFFICULTY_MIX_LEVELS
                    .map((level) => `${level} ${metadata.difficulty_generated[level] || 0}`)
                    .join(' · ')}
                </span>
              )}
              {typeof metadata.elapsed_s === 'number' && (
                <span>{metadata.elapsed_s}s</span>
              )}
              {typeof metadata.quality_rejections === 'number' && metadata.quality_rejections > 0 && (
                <span>{metadata.quality_rejections} auto-replaced</span>
              )}
              {kindCounts.subjective > 0 && (
                <>
                  <span className="kind-objective">{kindCounts.objective} objective</span>
                  <span className="kind-subjective">{kindCounts.subjective} subjective</span>
                </>
              )}
            </div>

            {isMixedBoardSet && (
              <div className="test-kind-card">
                <p className="test-kind-title">How should these questions become a test?</p>
                <p className="test-kind-text">
                  aulasAi generated <b>{kindCounts.objective} objective</b> and <b>{kindCounts.subjective} subjective</b>{' '}
                  (short / long answer, case-based) questions. The test type decides which questions are carried over.
                </p>
                <div className="test-kind-options">
                  {TEST_KIND_OPTIONS.map((opt) => {
                    const count = opt.value === 'Objective'
                      ? kindCounts.objective
                      : opt.value === 'Subjective' ? kindCounts.subjective : kindCounts.objective + kindCounts.subjective;
                    const disabled = onlyObjectiveAllowed && opt.value !== 'Objective';
                    const description = opt.value === 'Objective'
                      ? `Keeps the objective questions; the ${kindCounts.subjective} subjective ones are left out.`
                      : opt.value === 'Subjective'
                        ? `Keeps the subjective questions; the ${kindCounts.objective} objective ones are left out.`
                        : 'Keeps every question; each one keeps its own type.';
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`test-kind-option ${effectiveTestKind === opt.value ? 'active' : ''}`}
                        disabled={disabled}
                        aria-pressed={effectiveTestKind === opt.value}
                        onClick={() => setTestKind(opt.value)}
                      >
                        <b>{opt.label} ({count}){opt.value === 'Mixed' ? ' · recommended' : ''}</b>
                        <span>{description}</span>
                      </button>
                    );
                  })}
                </div>
                {onlyObjectiveAllowed && (
                  <p className="test-kind-hint">
                    Several subjects are selected, so only an Objective test is possible. Subjective and Mixed tests take one subject.
                  </p>
                )}
                {needsSubjectiveMarks && (
                  <div className="test-kind-marks">
                    <label htmlFor="subjective-marks">
                      Marks for each subjective question<span className="mandatory-star">*</span>
                    </label>
                    <Input
                      id="subjective-marks"
                      value={subjectiveMarks}
                      onChange={(e) => setSubjectiveMarks(e.target.value.replace(/[^0-9.]/g, ''))}
                      style={{ width: 80 }}
                    />
                    <span className="test-kind-hint">
                      Used for subjective questions that came without marks. No negative marks on subjective questions.
                    </span>
                  </div>
                )}
              </div>
            )}
            {!isMixedBoardSet && needsSubjectiveMarks && (
              <div className="test-kind-card">
                <div className="test-kind-marks" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                  <label htmlFor="subjective-marks">
                    Marks for each subjective question<span className="mandatory-star">*</span>
                  </label>
                  <Input
                    id="subjective-marks"
                    value={subjectiveMarks}
                    onChange={(e) => setSubjectiveMarks(e.target.value.replace(/[^0-9.]/g, ''))}
                    style={{ width: 80 }}
                  />
                  <span className="test-kind-hint">
                    Some questions came without marks. No negative marks on subjective questions.
                  </span>
                </div>
              </div>
            )}

            {/* Question Allocation (supports nested per-subject and flat shapes) */}
            {metadata.allocation && Object.keys(metadata.allocation).length > 0 && (
              <div className="allocation-panel">
                <h4>Question Allocation</h4>
                {typeof Object.values(metadata.allocation)[0] === 'object' ? (
                  Object.entries(metadata.allocation).map(([subj, chAlloc]) => (
                    <div key={subj} className="allocation-subject-group">
                      <div className="allocation-subject-header">{subj}</div>
                      {Object.entries(chAlloc || {}).map(([chapter, count]) => (
                        <div key={chapter} className="allocation-row">
                          <span>{chapter}</span>
                          <span className="allocation-count">{count}</span>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  Object.entries(metadata.allocation).map(([chapter, count]) => (
                    <div key={chapter} className="allocation-row">
                      <span>{chapter}</span>
                      <span className="allocation-count">{count}</span>
                    </div>
                  ))
                )}
              </div>
            )}

            {questions.length === 0 ? (
              <div className="warning-message">
                <p>
                  {metadata.n_requested
                    ? `The service returned 0 of the ${metadata.n_requested} questions requested.`
                    : 'The service returned no questions.'}{' '}
                  Nothing was generated for this selection — retry, or narrow the
                  chapters and try again.
                </p>
                {lastPayload && (
                  <button type="button" className="retry-btn" onClick={() => runGeneration(lastPayload)}>
                    Retry generation
                  </button>
                )}
              </div>
            ) : (
              shortfall && (
                <div className="warning-message">
                  <p>
                    The service generated {metadata.n_generated} of the{' '}
                    {metadata.n_requested} questions requested.
                  </p>
                  {lastPayload && (
                    <button type="button" className="retry-btn" onClick={() => runGeneration(lastPayload)}>
                      Retry for the full set
                    </button>
                  )}
                </div>
              )
            )}

            {questions.length > 0 ? (
              <>
                {/* Section filter tabs for multi-subject papers */}
                {usePaperSections && paperSections.length > 1 && (
                  <div className="section-tabs-bar">
                    <button
                      type="button"
                      className={`section-tab-btn ${activeSectionFilter === 'ALL' ? 'active' : ''}`}
                      onClick={() => setActiveSectionFilter('ALL')}
                    >
                      All ({keptQuestions.length})
                    </button>
                    {paperSections.map((sec) => {
                      const filterKey = `PS:${sec.section}`;
                      const secCount = keptCountOf(sec.question_ids);
                      if (secCount === 0) return null;
                      return (
                        <button
                          key={filterKey}
                          type="button"
                          className={`section-tab-btn ${activeSectionFilter === filterKey ? 'active' : ''}`}
                          onClick={() => setActiveSectionFilter(filterKey)}
                          title={sec.title}
                        >
                          Section {sec.section} ({secCount})
                        </button>
                      );
                    })}
                  </div>
                )}
                {!usePaperSections && sections.length > 1 && (
                  <div className="section-tabs-bar">
                    <button
                      type="button"
                      className={`section-tab-btn ${activeSectionFilter === 'ALL' ? 'active' : ''}`}
                      onClick={() => setActiveSectionFilter('ALL')}
                    >
                      All ({keptQuestions.length})
                    </button>
                    {sections.map((sec, secIdx) => {
                      const secLetter = SECTION_LETTERS[secIdx] || String(secIdx + 1);
                      const secCount = keptCountOf(sec.question_ids);
                      const isActive = activeSectionFilter === sec.subject;
                      return (
                        <button
                          key={sec.subject}
                          type="button"
                          className={`section-tab-btn ${isActive ? 'active' : ''}`}
                          onClick={() => setActiveSectionFilter(sec.subject)}
                        >
                          Section {secLetter}: {sec.subject} ({secCount})
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="regeneration-bar">
                  <div className="regeneration-bar-left">
                    <Checkbox
                      checked={isAllDisplayedSelected}
                      indeterminate={hasSomeDisplayedSelected && !isAllDisplayedSelected}
                      disabled={isRegenerating || displayedQuestions.length === 0}
                      onChange={handleToggleSelectAll}
                      className="question-checkbox"
                    >
                      <span className="select-all-label">
                        Select All ({displayedQuestions.length})
                      </span>
                    </Checkbox>
                    {isRegenerating && (
                      <span className="regeneration-in-progress-note">
                        <Spin size="small" style={{ marginRight: 6 }} />
                        Regenerating {regeneratingIds.length} question{regeneratingIds.length > 1 ? 's' : ''}...
                      </span>
                    )}
                  </div>
                  <div className="regeneration-bar-right">
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      disabled={isRegenerating || selectedQuestionIds.length === 0}
                      onClick={() => handleRegenerate(selectedQuestionIds, true)}
                      className="radius-100 regenerate-selected-btn"
                    >
                      Regenerate Selected {selectedQuestionIds.length > 0 ? `(${selectedQuestionIds.length})` : ''}
                    </Button>
                  </div>
                </div>

                <div className="mcqs-container">
                  {displayedQuestions.map((question, index) => {
                    const qId = Number(question.id);
                    const isSelected = selectedQuestionIds.includes(qId);
                    const isRecentlyRegenerated = recentlyRegeneratedIds.includes(qId);
                    const qType = String(question.question_type || question.type || '').toLowerCase();
                    const isWrittenAnswer = WRITTEN_ANSWER_TYPES.includes(qType);
                    const isCaseBased = qType === 'case_based';
                    const isBoardWritten = isWrittenAnswer || isCaseBased;
                    const isSubjective = isSubjectiveQuestion(question);
                    const typeLabel = (isBoardPattern || usePaperSections
                      ? BOARD_QUESTION_TYPES.find((t) => t.value === qType)?.label
                      : null)
                      || QUESTION_TYPES.find((t) => t.value === resolveQuestionType(question, selection.question_types))?.label
                      || (isSubjective ? 'Written answer' : null);
                    const subQuestions = isCaseBased && Array.isArray(question.sub_questions)
                      ? question.sub_questions
                      : [];
                    const markingPoints = normalizeMarkingPoints(question.marking_points);
                    // Banking Mains (B2.1): the two prompts of a descriptive task; the student answers one.
                    const descChoices = descriptiveChoices(question);
                    const isInteger = !isBoardWritten && isIntegerQuestion(question, selection.question_types);
                    const answer = !isInteger && !isBoardWritten ? answerText(question) : null;
                    const matrixCols = getMatrixColumns(question);
                    const qMarks = isSubjective
                      ? (getSubjectiveMarks(question) ?? (subjectiveMarksValid ? subjectiveMarksValue : null))
                      : getQuestionMarks(question, generatedTest, selection.question_types);
                    const qNegMarks = isSubjective
                      ? null
                      : getQuestionNegativeMarks(question, generatedTest, selection.question_types);
                    const diagram =
                      question.diagram && question.diagram.required
                        ? diagramUrl(question.diagram.rendered_url)
                        : null;

                    // Section divider heading when "All" is active and transitioning to a new section
                    const prevQ = displayedQuestions[index - 1];
                    // Board: headings come from paper_sections ("Section A — title · 16 marks").
                    const paperSection = usePaperSections ? paperSectionByQuestionId.get(qId) : null;
                    const prevPaperSection = usePaperSections && prevQ
                      ? paperSectionByQuestionId.get(Number(prevQ.id))
                      : null;
                    const isPaperSectionStart =
                      usePaperSections &&
                      activeSectionFilter === 'ALL' &&
                      !!paperSection &&
                      (!prevQ || prevPaperSection?.section !== paperSection.section);
                    const isSectionStart =
                      !usePaperSections &&
                      activeSectionFilter === 'ALL' &&
                      sections.length > 1 &&
                      (!prevQ || prevQ.subject !== question.subject);
                    const secIndex = isSectionStart ? sections.findIndex((s) => s.subject === question.subject) : -1;
                    const secLetter = secIndex >= 0 ? SECTION_LETTERS[secIndex] : '';
                    const secCount = secIndex >= 0 && sections[secIndex].question_ids ? sections[secIndex].question_ids.length : '';
                    // Banking set: its passage, table or puzzle is shown once, above the first of its questions.
                    const questionSet = question.set_id ? questionSetById.get(question.set_id) : null;
                    const isSetStart = !!questionSet && (!prevQ || prevQ.set_id !== question.set_id || isPaperSectionStart);
                    const setIds = questionSet && Array.isArray(questionSet.question_ids) ? questionSet.question_ids : [];
                    const setRange = setIds.length > 1
                      ? `Questions #${setIds[0]}–#${setIds[setIds.length - 1]}`
                      : `Question #${question.id}`;

                    return (
                      <React.Fragment key={question.id || index}>
                        {isPaperSectionStart && (
                          <div className="section-divider-heading">
                            <div className="section-divider-title">
                              <span className="section-divider-pill">{paperSectionLabel(paperSection)}</span>
                              {!bankingPaper && <span className="section-divider-name">{paperSection.title}</span>}
                            </div>
                            {paperSection.marks !== undefined && paperSection.marks !== null && (
                              <span className="section-divider-count">
                                {bankingPaper && `${paperSection.question_ids.length} questions · `}
                                {formatMarks(paperSection.marks)} marks
                                {/* Banking sections are timed separately, and a finished section cannot be reopened */}
                                {paperSection.time_minutes ? ` · ${paperSection.time_minutes} min` : ''}
                                {/* Mains marks differ by section, so each header carries its own scheme */}
                                {/* (null for the Descriptive Test, whose tasks carry different marks: shown as —) */}
                                {bankingPaper
                                  ? ` · ${markingSchemeText(paperSection.marking_scheme) ? `${markingSchemeText(paperSection.marking_scheme)} per question` : '—'}`
                                  : ''}
                              </span>
                            )}
                          </div>
                        )}
                        {questionSet && isSetStart && (
                          <div className="question-set-block">
                            <div className="question-set-label">
                              {setRange} · {questionSet.topic}
                            </div>
                            {questionSet.directions && (
                              <div className="question-set-directions board-multiline">
                                <MathText>{questionSet.directions}</MathText>
                              </div>
                            )}
                            {questionSet.stimulus && (
                              <div className="question-set-stimulus board-multiline">
                                <MathText>{questionSet.stimulus}</MathText>
                              </div>
                            )}
                            {questionSet.table && Array.isArray(questionSet.table.columns) && (
                              <div className="question-set-table-wrap">
                                {questionSet.table.title && (
                                  <div className="question-set-table-title">
                                    <MathText>{questionSet.table.title}</MathText>
                                  </div>
                                )}
                                <table className="question-set-table">
                                  <thead>
                                    <tr>
                                      {questionSet.table.columns.map((col, colIdx) => (
                                        <th key={colIdx}><MathText>{col}</MathText></th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(questionSet.table.rows || []).map((row, rowIdx) => (
                                      <tr key={rowIdx}>
                                        {(Array.isArray(row) ? row : [row]).map((cell, cellIdx) => (
                                          <td key={cellIdx}><MathText>{cell}</MathText></td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {/* The set's worked solution is for the teacher's answer key only */}
                            {questionSet.solution && (
                              <details className="question-set-solution">
                                <summary>Solution for this set</summary>
                                <div className="board-multiline">
                                  <MathText>{questionSet.solution}</MathText>
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                        {isSectionStart && (
                          <div className="section-divider-heading">
                            <div className="section-divider-title">
                              <span className="section-divider-pill">Section {secLetter}</span>
                              <span className="section-divider-name">{question.subject}</span>
                            </div>
                            {secCount && (
                              <span className="section-divider-count">{secCount} questions</span>
                            )}
                          </div>
                        )}
                        <div
                          className={`mcq-card ${isSelected ? 'mcq-card-selected' : ''} ${isRecentlyRegenerated ? 'mcq-card-regenerated' : ''} ${questionSet ? 'mcq-card-in-set' : ''}`}
                        >
                          <div className="mcq-header">
                            <div className="mcq-header-left">
                              <Checkbox
                                checked={isSelected}
                                disabled={isRegenerating}
                                onChange={() => handleToggleQuestionSelect(question.id)}
                                className="question-checkbox"
                              />
                              <div className="question-number">Question #{keptNumberById.get(qId) || index + 1}</div>
                              {isRecentlyRegenerated && (
                                <span className="regenerated-badge">
                                  <CheckCircleOutlined style={{ marginRight: 4 }} />
                                  Regenerated
                                </span>
                              )}
                            </div>
                            <div className="mcq-header-right">
                              <div className="question-badges">
                                {question.subject && (
                                  <span className="question-topic subject-pill">{question.subject}</span>
                                )}
                                {question.chapter && (
                                  <span className="question-topic">{question.chapter}</span>
                                )}
                                {question.topic && (
                                  <span className="question-topic">{question.topic}</span>
                                )}
                                {question.subtopic && (
                                  <span className="question-topic">{question.subtopic}</span>
                                )}
                                {typeLabel && (
                                  <span className={`question-topic ${isSubjective ? 'kind-subjective' : 'kind-objective'}`}>
                                    {typeLabel}{isSubjective ? ' · Subjective' : ''}
                                  </span>
                                )}
                                {qType === 'descriptive' && (
                                  <span className="question-topic">Descriptive</span>
                                )}
                                {question.word_limit ? (
                                  <span className="question-topic">Word limit: {question.word_limit}</span>
                                ) : null}
                                {question.difficulty && (
                                  <span className="question-topic">
                                    {String(question.difficulty).charAt(0).toUpperCase() + String(question.difficulty).slice(1)}
                                  </span>
                                )}
                                {qMarks !== null && qMarks !== undefined && (
                                  <span className="question-topic">{qMarks} {Number(qMarks) === 1 ? 'mark' : 'marks'}</span>
                                )}
                                {qNegMarks !== null && qNegMarks !== undefined && Number(qNegMarks) !== 0 && (
                                  <span className="question-topic negative-marks-pill" title={`Negative marking: -${Math.abs(Number(qNegMarks))}`}>
                                    -{Math.abs(Number(qNegMarks))} {Math.abs(Number(qNegMarks)) === 1 ? 'mark' : 'marks'}
                                  </span>
                                )}
                              </div>
                              <Button
                                size="small"
                                className="radius-100 regenerate-single-btn"
                                icon={<ReloadOutlined />}
                                disabled={isRegenerating}
                                onClick={() => handleRegenerate([question.id], false)}
                                // A question in a set cannot be replaced alone: the backend redoes the whole set.
                                title={questionSet && setIds.length > 1 ? `Regenerates the whole set (${setIds.length} questions)` : undefined}
                              >
                                Regenerate
                              </Button>
                            </div>
                          </div>
                          <div className="mcq-body">
                            {/* Board questions keep their line breaks: assertion/reason lines, case passages. */}
                            {/* Banking: the exam's own instruction line for this question */}
                            {question.directions && (
                              <div className="question-directions board-multiline">
                                <MathText>{question.directions}</MathText>
                              </div>
                            )}
                            <div className={`mcq-question ${usePaperSections ? 'board-multiline' : ''}`}>
                              <MathText>
                                {question.question || question.text || question.statement || ''}
                              </MathText>
                            </div>
                            {descChoices.length > 0 && (
                              <div className="descriptive-choices">
                                <div className="descriptive-choices-note">Answer any one of the following.</div>
                                {descChoices.map((choice) => (
                                  <div key={choice.label} className="descriptive-choice">
                                    <span className="descriptive-choice-letter">{choice.label}</span>
                                    <div className="board-multiline">
                                      <MathText>{choice.prompt}</MathText>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {subQuestions.length > 0 && (
                              <ol className="board-sub-questions">
                                {subQuestions.map((sub, subIdx) => (
                                  <li key={subIdx} className="board-sub-question">
                                    <span className="board-sub-question-number">
                                      ({ROMAN_NUMERALS[subIdx] || subIdx + 1})
                                    </span>
                                    <span className="board-sub-question-text board-multiline">
                                      <MathText>{sub.question || ''}</MathText>
                                    </span>
                                    {sub.marks !== undefined && sub.marks !== null && (
                                      <span className="board-marks-chip">
                                        {formatMarks(sub.marks)} {Number(sub.marks) === 1 ? 'mark' : 'marks'}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            )}
                            {/* Key Idea is hidden from the UI on purpose; kept for reference.
                            {question.key_idea && (
                              <div className="question-key-idea">
                                <span className="key-idea-label">Key Idea:</span>
                                <span className="key-idea-text">
                                  <MathText>{question.key_idea}</MathText>
                                </span>
                              </div>
                            )}
                            */}
                            {diagram && (
                              <div className="question-diagram">
                                <img
                                  src={diagram}
                                  alt={`Diagram for question ${index + 1}`}
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.parentElement.classList.add('failed');
                                  }}
                                />
                                <span className="diagram-fallback">
                                  Diagram could not be loaded
                                </span>
                              </div>
                            )}
                            {matrixCols.hasColumns && (
                              <div className="matrix-match-container">
                                <div className="matrix-column">
                                  <div className="matrix-column-header">
                                    <span className="matrix-column-title">List 1</span>
                                  </div>
                                  <div className="matrix-column-list">
                                    {matrixCols.colA.map((item, idx) => {
                                      const parsed = parseMatrixItem(item, DEFAULT_COL_A_LETTERS[idx] || OPTION_LETTERS[idx]);
                                      return (
                                        <div key={idx} className="matrix-item">
                                          {parsed.label && <span className="matrix-item-label">{parsed.label}</span>}
                                          <div className="matrix-item-content">
                                            <MathText>{parsed.text}</MathText>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="matrix-column">
                                  <div className="matrix-column-header">
                                    <span className="matrix-column-title">List 2</span>
                                  </div>
                                  <div className="matrix-column-list">
                                    {matrixCols.colB.map((item, idx) => {
                                      const parsed = parseMatrixItem(item, DEFAULT_COL_B_LETTERS[idx]);
                                      return (
                                        <div key={idx} className="matrix-item">
                                          {parsed.label && <span className="matrix-item-label">{parsed.label}</span>}
                                          <div className="matrix-item-content">
                                            <MathText>{parsed.text}</MathText>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            )}
                            {!isInteger && question.options && (Array.isArray(question.options) ? question.options.length > 0 : Object.keys(question.options).length > 0) && (
                              <div className="mcq-options">
                                {renderOptions(question.options, question)}
                              </div>
                            )}
                            <div className="mcq-answer">
                              <div className="answer-label">
                                {isBoardWritten ? 'Answer Key:' : 'Correct Answer:'}
                              </div>
                              {isWrittenAnswer ? (
                                // One key per choice for a two-choice descriptive task, otherwise one.
                                (descChoices.length > 0
                                  ? descChoices
                                  : [{ label: '', modelAnswer: question.model_answer || question.correct_answer, markingPoints }]
                                ).map((key) => (
                                  <div key={key.label || 'key'} className="board-answer-key">
                                    {key.label && (
                                      <div className="descriptive-choice-label">Choice {key.label}</div>
                                    )}
                                    <div className="board-model-answer board-multiline">
                                      <MathText>
                                        {key.modelAnswer || 'N/A'}
                                      </MathText>
                                    </div>
                                    {key.markingPoints.length > 0 && (
                                      <div className="board-marking-points">
                                        <div className="board-marking-points-title">Marking points</div>
                                        <ul>
                                          {key.markingPoints.map((mp, mpIdx) => (
                                            <li key={mpIdx}>
                                              <span className="board-marking-point-text">
                                                <MathText>{mp.point || ''}</MathText>
                                              </span>
                                              <span className="board-marks-chip">{formatMarks(mp.marks)}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                ))
                              ) : isCaseBased ? (
                                <div className="board-answer-key">
                                  {subQuestions.length > 0 ? (
                                    <ol className="board-sub-answers">
                                      {subQuestions.map((sub, subIdx) => (
                                        <li key={subIdx}>
                                          <span className="board-sub-question-number">
                                            ({ROMAN_NUMERALS[subIdx] || subIdx + 1})
                                          </span>
                                          <span className="board-multiline">
                                            <MathText>{sub.answer || ''}</MathText>
                                          </span>
                                        </li>
                                      ))}
                                    </ol>
                                  ) : (
                                    <div className="board-model-answer board-multiline">
                                      <MathText>{question.correct_answer || 'N/A'}</MathText>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="answer-value">
                                  {answer && typeof answer === 'object' && answer.isMatrixMapping ? (
                                    <div className="matrix-correct-mapping">
                                      {answer.pairs.map((pair, idx) => (
                                        <div key={idx} className="matrix-mapping-badge">
                                          <span className="matrix-mapping-left">{pair.left}</span>
                                          <span className="matrix-mapping-arrow">→</span>
                                          <span className="matrix-mapping-right">{pair.right}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : isInteger ? (
                                    <span className="answer-text">
                                      <MathText>
                                        {String(question?.correct_answer ?? question?.answer ?? 'N/A')}
                                      </MathText>
                                    </span>
                                  ) : answer && typeof answer === 'object' ? (
                                    answer.isMultiple ? (
                                      <div className="multi-correct-answers" style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {answer.letters.map((ansLetter) => (
                                              <span
                                                key={ansLetter}
                                                className="answer-letter"
                                                style={{ width: 28, height: 28, borderRadius: '50%', padding: 0 }}
                                              >
                                                {ansLetter}
                                              </span>
                                            ))}
                                          </div>
                                          <span style={{ fontSize: 13, color: '#594099', fontWeight: 600 }}>
                                            (Multiple Correct)
                                          </span>
                                        </div>
                                        {answer.items && answer.items.length > 0 && (
                                          <div className="multi-answer-items" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                                            {answer.items.map((item) => (
                                              <div key={item.letter} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                <span
                                                  className="answer-letter"
                                                  style={{ width: 24, height: 24, fontSize: 12, borderRadius: '50%', padding: 0, flexShrink: 0 }}
                                                >
                                                  {item.letter}
                                                </span>
                                                <span className="answer-text" style={{ fontSize: 14, color: '#333', lineHeight: 1.5 }}>
                                                  <MathText>{item.value}</MathText>
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <>
                                        <span className="answer-letter">{answer.letter}</span>
                                        <span className="answer-text">
                                          <MathText>{answer.value}</MathText>
                                        </span>
                                      </>
                                    )
                                  ) : (
                                    <span className="answer-text">{answer || 'N/A'}</span>
                                  )}
                                </div>
                              )}
                              {(question.explanation || question.solution) && (
                                <div className="answer-explanation">
                                  <MathText>
                                    {question.explanation || question.solution}
                                  </MathText>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </ConfigProvider>
  );
};

const mapStateToProps = (state) => {
  const { loaders } = state.testAdmin;
  return { handoffLoading: !!loaders?.aiHandoffLoader };
};

const mapDispatchToProps = (dispatch) => ({
  startAiTestHandoff: (selection, generatedTest, callback) =>
    dispatch(startAiTestHandoff(selection, generatedTest, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TestGenerationTab);
